import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    ArrowLeft, User as UserIcon, LogOut,
    ChevronRight, ChevronDown, ExternalLink, Clock, Users, BarChart2,
    Search, X, AlertCircle, Database, Inbox, PlusCircle,
    CheckCircle2, Circle, Sparkles, Briefcase, Loader2, SlidersHorizontal, Network, Lock,
    Bell, Mail, Filter, Settings, Zap
} from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import ThemeToggle from './ThemeToggle';
import { getLinkedinOrgIdsByCompanyNames, getCompanyJobsSummary, getCompanyInsights, getProfile, captureOrgDestinationMap } from '../api/insights';
import { toast } from 'sonner';
import CompanyPeoplePanel from './CompanyPeoplePanel';
import NetworkMapView from './NetworkMapView';
import ProfileGlobalMapView from './ProfileGlobalMapView';
import LocationHiringView from './LocationHiringView';
import { trackCompanyClicked, trackFindingJobs, trackInsightBlockClicked } from '../analytics/mixpanel';
/* ── helpers ── */
const avatarColor = (name = '') => {
    const cols = ['#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#EF4444', '#14B8A6'];
    let h = 0;
    for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
    return cols[Math.abs(h) % cols.length];
};
const buildLinkedInJobsUrl = (companyName) =>
    `https://www.linkedin.com/jobs/search/?keywords=${encodeURIComponent(companyName)}`;
const buildContactSearchUrl = (companyName, sourceCompany) => {
    const q = [sourceCompany, companyName, 'linkedin'].filter(Boolean).join(' ');
    return `https://www.google.com/search?q=${encodeURIComponent(q)}`;
};
const getJobSearchStorageKey = (userId) => `insights_job_searches_${userId || 'anon'}`;
const readJobSearches = (userId) => {
    if (typeof window === 'undefined') return {};
    try {
        const raw = localStorage.getItem(getJobSearchStorageKey(userId));
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};
const getSavedRolesStorageKey = (userId) => `insights_saved_roles_${userId || 'anon'}`;
const readSavedRoles = (userId) => {
    if (typeof window === 'undefined') return [];
    try {
        const raw = localStorage.getItem(getSavedRolesStorageKey(userId));
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};
const writeSavedRoles = (userId, roles) => {
    if (typeof window === 'undefined') return;
    try {
        const uniqueRoles = [...new Set(roles)].filter(Boolean).slice(0, 15);
        localStorage.setItem(getSavedRolesStorageKey(userId), JSON.stringify(uniqueRoles));
    } catch {
        // ignore
    }
};
const writeJobSearches = (userId, data) => {
    if (typeof window === 'undefined') return;
    try {
        localStorage.setItem(getJobSearchStorageKey(userId), JSON.stringify(data || {}));
    } catch {
        // ignore storage failures
    }
};
const getDismissedNewTagsStorageKey = (userId) => `insights_dismissed_new_tags_${userId || 'anon'}`;
const readDismissedNewTags = (userId) => {
    if (typeof window === 'undefined') return {};
    try {
        const raw = localStorage.getItem(getDismissedNewTagsStorageKey(userId));
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};
const writeDismissedNewTags = (userId, data) => {
    if (typeof window === 'undefined') return;
    try {
        localStorage.setItem(getDismissedNewTagsStorageKey(userId), JSON.stringify(data || {}));
    } catch {
        // ignore storage failures
    }
};
const INSIGHTS_LIST_CONTENT_MAX_WIDTH = 940;
const INSIGHTS_GLOBAL_NETWORK_MAX_WIDTH = 1040;
const FeatureNewBadge = ({ isDark, tooltip, compact = false }) => {
    const [open, setOpen] = useState(false);
    return (
        <span
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
            style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: compact ? 0 : 2 }}
        >
            <span
                style={{
                    fontSize: compact ? 9 : 10,
                    fontWeight: 900,
                    letterSpacing: '0.05em',
                    padding: compact ? '2px 7px' : '2px 8px',
                    borderRadius: 999,
                    background: isDark ? 'linear-gradient(135deg, rgba(16,185,129,0.26), rgba(5,150,105,0.18))' : 'linear-gradient(135deg, rgba(16,185,129,0.2), rgba(5,150,105,0.1))',
                    border: isDark ? '1px solid rgba(52,211,153,0.55)' : '1px solid rgba(5,150,105,0.34)',
                    color: isDark ? '#6EE7B7' : '#065F46',
                    textTransform: 'uppercase',
                    boxShadow: isDark ? '0 0 0 1px rgba(6,78,59,0.25), 0 4px 14px rgba(16,185,129,0.16)' : '0 0 0 1px rgba(16,185,129,0.12), 0 4px 12px rgba(16,185,129,0.12)',
                }}
            >
                New
            </span>
            <span
                aria-hidden
                style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    marginLeft: 4,
                    background: isDark ? '#34D399' : '#059669',
                    boxShadow: isDark ? '0 0 10px rgba(52,211,153,0.7)' : '0 0 8px rgba(5,150,105,0.45)',
                }}
            />
            {open && (
                <span
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 8px)',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 230,
                        zIndex: 60,
                        padding: '8px 10px',
                        borderRadius: 10,
                        background: isDark ? '#111827' : '#ffffff',
                        border: isDark ? '1px solid rgba(52,211,153,0.35)' : '1px solid rgba(5,150,105,0.25)',
                        boxShadow: isDark ? '0 16px 30px rgba(0,0,0,0.42)' : '0 12px 24px rgba(15,23,42,0.18)',
                        color: isDark ? 'rgba(255,255,255,0.9)' : '#334155',
                        fontSize: 11,
                        fontWeight: 600,
                        lineHeight: 1.45,
                        letterSpacing: '0.01em',
                    }}
                >
                    {tooltip}
                </span>
            )}
        </span>
    );
};
const companyKeyFromItem = (item) => {
    const orgId = item?.organization_id;
    if (orgId != null && !Number.isNaN(Number(orgId))) return `org:${Number(orgId)}`;
    const name = String(item?.organization || item || '').trim().toLowerCase();
    if (!name) return null;
    return `name:${name}`;
};
const formatJobSearchAgo = (diffMs) => {
    const mins = Math.max(0, Math.floor(diffMs / 60000));
    if (mins < 60) {
        const value = Math.max(1, mins);
        return `${value} min${value === 1 ? '' : 's'} ago`;
    }
    const hours = Math.floor(mins / 60);
    if (hours < 24) {
        return `${hours} hr${hours === 1 ? '' : 's'} ago`;
    }
    const days = Math.floor(hours / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
};
const mergeCareerPathList = (careerPaths) => {
    const hop2 = Array.isArray(careerPaths?.hop2) ? careerPaths.hop2 : [];
    const hop3 = Array.isArray(careerPaths?.hop3) ? careerPaths.hop3 : [];
    const byOrg = new Map();
    hop2.forEach((item) => {
        if (!item || item.organization_id == null) return;
        const prev = byOrg.get(item.organization_id);
        if (!prev) {
            byOrg.set(item.organization_id, {
                organization_id: item.organization_id,
                organization: item.organization,
                count: Number(item.count || 0),
                hop2_count: Number(item.count || 0),
                hop3_count: 0,
                dominant_hop: 2,
            });
            return;
        }
        prev.count += Number(item.count || 0);
        prev.hop2_count += Number(item.count || 0);
        prev.dominant_hop = (prev.hop2_count >= prev.hop3_count) ? 2 : 3;
    });
    hop3.forEach((item) => {
        if (!item || item.organization_id == null) return;
        const prev = byOrg.get(item.organization_id);
        if (!prev) {
            byOrg.set(item.organization_id, {
                organization_id: item.organization_id,
                organization: item.organization,
                count: Number(item.count || 0),
                hop2_count: 0,
                hop3_count: Number(item.count || 0),
                dominant_hop: 3,
            });
            return;
        }
        prev.count += Number(item.count || 0);
        prev.hop3_count += Number(item.count || 0);
        prev.dominant_hop = (prev.hop2_count >= prev.hop3_count) ? 2 : 3;
    });
    return Array.from(byOrg.values()).sort((a, b) => (
        Number(b.count || 0) - Number(a.count || 0) ||
        String(a.organization || '').localeCompare(String(b.organization || ''))
    ));
};
const getRelationLabels = (item) => {
    const tags = Array.isArray(item?.relation_tags) ? item.relation_tags : [];
    if (tags.length > 0) {
        return tags
            .map((t) => (typeof t === 'string' ? t : (t?.label || '')))
            .map((t) => String(t || '').trim())
            .filter(Boolean);
    }
    const raw = String(item?.strongest_relation || '').trim();
    if (!raw) return [];
    return raw.split('·').map((x) => String(x || '').trim()).filter(Boolean);
};
const classifyGlobalTier = (labels, scope) => {
    if (scope === 'work') {
        const role = labels.some((l) => /same role|similar role/i.test(l));
        const sameTen = labels.some((l) => /same tenure/i.test(l));
        const nearTen = labels.some((l) => /near tenure|±2 yrs/i.test(l));
        if (role && sameTen) return 'T1';
        if (role && nearTen) return 'T2';
        if (sameTen) return 'T3';
        if (nearTen) return 'T4';
        if (role) return 'T5';
        return 'T6';
    }
    const branch = labels.some((l) => /same branch|similar branch/i.test(l));
    const sameBatch = labels.some((l) => /same batch/i.test(l));
    const nearBatch = labels.some((l) => /near batch|±2 yrs/i.test(l));
    if (branch && sameBatch) return 'T1';
    if (branch && nearBatch) return 'T2';
    if (sameBatch) return 'T3';
    if (nearBatch) return 'T4';
    if (branch) return 'T5';
    return 'T6';
};
/* ── mode config ── */
const MODE_META = {
    work_network: {
        title: (c, _role, selectedWorkTab) =>
            selectedWorkTab && selectedWorkTab !== 'best'
                ? `People from ${selectedWorkTab} now inside these companies`
                : `Best work overlap for ${c}`,
        subtitle: (_role, selectedWorkTab) =>
            selectedWorkTab && selectedWorkTab !== 'best'
                ? 'Ranked by likelihood of getting a referral. Start with the top companies first.'
                : 'Ranked by likelihood of getting a referral. Start with the top companies first.',
        getItems: (d, _role, selectedWorkTab) => {
            const tabs = d?.network_work_tabs && typeof d.network_work_tabs === 'object' ? d.network_work_tabs : {};
            const key = selectedWorkTab || 'best';
            return Array.isArray(tabs[key]) ? tabs[key] : [];
        },
        getRightLabel: (item) => `${Number(item?.count || 0)} contacts`,
        getRightColor: () => '#34D399',
        getRightTitle: () => 'Contacts',
        getSubtext: () => '',
        getTags: (item) => {
            if (Array.isArray(item?.relation_tags) && item.relation_tags.length > 0) {
                return item.relation_tags;
            }
            const raw = String(item?.strongest_relation || '').trim();
            if (!raw) return [];
            return raw.split('·').map((x) => String(x || '').trim()).filter(Boolean).slice(0, 3);
        },
        badge: null,
        badgeColor: null,
    },
    education_network: {
        title: (c, _role, _selectedWorkTab, selectedCollegeTab) =>
            selectedCollegeTab && selectedCollegeTab !== 'best'
                ? `People from ${selectedCollegeTab} now inside these companies`
                : `Best college overlap for ${c}`,
        subtitle: (_role, _selectedWorkTab, selectedCollegeTab) =>
            selectedCollegeTab && selectedCollegeTab !== 'best'
                ? 'Ranked by likelihood of getting a referral. Start with the top companies first.'
                : 'Ranked by likelihood of getting a referral. Start with the top companies first.',
        getItems: (d, _role, _selectedWorkTab, selectedCollegeTab) => {
            const tabs = d?.network_education_tabs && typeof d.network_education_tabs === 'object' ? d.network_education_tabs : {};
            const key = selectedCollegeTab || 'best';
            return Array.isArray(tabs[key]) ? tabs[key] : [];
        },
        getRightLabel: (item) => `${Number(item?.count || 0)} contacts`,
        getRightColor: () => '#8B5CF6',
        getRightTitle: () => 'Contacts',
        getSubtext: () => '',
        getTags: (item) => {
            if (Array.isArray(item?.relation_tags) && item.relation_tags.length > 0) {
                return item.relation_tags;
            }
            const raw = String(item?.strongest_relation || '').trim();
            if (!raw) return [];
            return raw.split('·').map((x) => String(x || '').trim()).filter(Boolean).slice(0, 3);
        },
        badge: null,
        badgeColor: null,
    },
    cross_network: {
        title: () => 'Where you have leverage from both sides',
        subtitle: 'Ranked by likelihood of getting a referral. Start with the top companies first.',
        getItems: (d) => d?.network_cross_companies || [],
        getRightLabel: (item) => `${Number(item?.count || 0)} matches`,
        getRightColor: () => '#F59E0B',
        getRightTitle: () => 'Matches',
        getSubtext: () => '',
        getTags: (item) => {
            const entities = Array.isArray(item?.strongest_entities)
                ? item.strongest_entities
                    .map((e) => String(e || '').trim())
                    .filter(Boolean)
                    .slice(0, 2)
                : [];
            if (entities.length > 0) return entities;
            // Fallback: extract only values from "Work: X" / "College: Y"
            const relationTags = Array.isArray(item?.relation_tags) ? item.relation_tags : [];
            const vals = relationTags
                .map((t) => typeof t === 'string' ? t : (t?.label || ''))
                .map((s) => String(s || '').trim())
                .filter(Boolean)
                .map((label) => {
                    if (/^work\s*:/i.test(label)) return label.replace(/^work\s*:/i, '').trim();
                    if (/^college\s*:/i.test(label)) return label.replace(/^college\s*:/i, '').trim();
                    return '';
                })
                .filter(Boolean);
            return [...new Set(vals)].slice(0, 2);
        },
        badge: null,
        badgeColor: null,
    },
    global_network: {
        title: () => 'Network Map',
        subtitle: () => 'Click your company/college nodes to load related companies below.',
        getItems: () => [],
        getRightLabel: () => '',
        getRightColor: () => '#60A5FA',
        getRightTitle: () => '',
        getSubtext: () => '',
        getTags: () => [],
        badge: null,
        badgeColor: null,
    },
    active_hirers: {
        title: (c) => `Companies actively hiring from ${c} right now`,
        subtitle: 'Sorted by how recently they hired. Pick a company — we\'ll find you a real person inside to talk to.',
        getItems: (d) => d?.active_hirers || [],
        getRightLabel: (item) => item.last_hire_label || '',
        getRightColor: () => '#10B981',
        getRightTitle: () => 'Last hire',
        getSubtext: (item, sourceCompany) =>
            `Recently hired ${item.count} ${sourceCompany} alumni ${item.last_hire_label}.`,
    },
    next_destinations: {
        title: (c) => `Where ${c} people are actually landing in big numbers`,
        subtitle: (sourceCompany) => `Sorted by number of alumni. These companies have proven track records of hiring from ${sourceCompany}.`,
        getItems: (d) => d?.next_destinations || [],
        getRightLabel: (item) => `${item.count} people`,
        getRightColor: () => '#F59E0B',
        getRightTitle: () => 'Alumni moved here',
        getSubtext: (item, sourceCompany) =>
            `${item.count} ${sourceCompany} alumni made this their 1st move.`,
        badge: null,
        badgeColor: null,
    },
    career_paths: {
        title: (c) => `Where ${c} alumni go after their first move`,
        subtitle: 'A combined list of destinations after the first move.',
        getItems: (d) => mergeCareerPathList(d?.career_paths),
        getRightLabel: (item) => `${item.count} people`,
        getRightColor: () => '#8B5CF6',
        getRightTitle: () => 'People',
        getSubtext: (item, sourceCompany) =>
            `${item.count} ${sourceCompany} alumni landed here after their next company.`,
        badge: null,
        badgeColor: null,
    },
    best_tenure: {
        title: (c) => `Companies where ${c} alumni stayed the longest`,
        subtitle: 'Ranked by average tenure. Long stays = company treated them well.',
        getItems: (d) => d?.best_tenure || [],
        getRightLabel: (item) => `avg ${item.avg_tenure_years} yrs`,
        getRightColor: () => '#10B981',
        getRightTitle: () => 'Avg tenure',
        getSubtext: (item, sourceCompany) =>
            `${sourceCompany} alumni stayed an average of ${item.avg_tenure_years} years. Worth pursuing in parallel.`,
        badge: null,
        badgeColor: null,
    },
    role_hiring: {
        title: (c, role) => role
            ? `Where ${role} from ${c} are getting hired`
            : `Where ${c} alumni are getting hired`,
        pillLabel: (role) => `${role || 'Role'} Hiring`,
        subtitle: (role) => `Filtered by role. These companies specifically hired from here for this function.`,
        getItems: (d, role) => (role && d?.role_hiring?.length ? d.role_hiring : d?.next_destinations || []),
        getRightLabel: (item) => `${item.count} people`,
        getRightColor: () => '#3B82F6',
        getRightTitle: () => 'Hires',
        getSubtext: (item, sourceCompany, role) =>
            `${item.count}  ${role || 'this role'} from ${sourceCompany} moved here.`,
        getTags: () => [],
        badge: null,
        badgeColor: null,
    },
    location_hiring: {
        title: (c, role, workTab, collTab, loc) => loc
            ? `Where ${c} alumni are getting hired in ${loc}`
            : `Where ${c} alumni are getting hired`,
        pillLabel: (loc) => loc ? `${loc} Hiring` : 'Location Hiring',
        subtitle: (role, workTab, collTab, loc) => `Filtered by location. These companies specifically hired from here in this area.`,
        getItems: (d, role, workTab, collTab, loc) => {
            // For "All Locations" (loc is empty), prefer location_hiring from location-only fetches.
            // Fallback to next_destinations when present (e.g. full payload).
            if (!loc) {
                const companies = d?.location_hiring || d?.next_destinations || [];
                return [...companies].sort((a, b) => (b.count || 0) - (a.count || 0)).slice(0, 30);
            }
            // For specific cities, the fetched data puts the companies in location_hiring
            return d?.location_hiring || d?.next_destinations || [];
        },
        getRightLabel: (item) => `${item.count} people`,
        getRightColor: () => '#10B981',
        getRightTitle: () => 'Hires',
        getSubtext: (item, sourceCompany, role, workTab, collTab, loc) =>
            `${item.count} ${sourceCompany} alumni moved here. Direct relevance to your target location.`,
        getTags: () => [],
        badge: null,
        badgeColor: null,
    },
};
const FREE_COMPANY_LIMIT = 3;
const PREMIUM_COMPANY_LIMIT = 30;
const GLOBAL_WORK_TIER_LABELS = {
    T1: 'Same company · Similar role · Same tenure',
    T2: 'Same company · Similar role · ±2 yrs',
    T3: 'Same company · Same tenure',
    T4: 'Same company · ±2 yrs',
    T5: 'Same company · Similar role',
    T6: 'Same company background',
};
const GLOBAL_EDU_TIER_LABELS = {
    T1: 'Same college · Same branch · Same batch',
    T2: 'Same college · Same branch · ±2 yrs',
    T3: 'Same college · Same batch · Diff branch',
    T4: 'Same college · ±2 yrs · Diff branch',
    T5: 'Same college · Same branch',
    T6: 'Same college background',
};
MODE_META.college_network = MODE_META.education_network;
const InsightsList = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout, loading: authLoading } = useAuth();
    const { isDark } = useTheme();
    const appCls = isDark ? 'dark-app' : 'light-app';
    const accentColor = isDark ? '#F59E0B' : '#2563eb';
    const bgPage = isDark ? 'rgba(13,16,25,0.92)' : 'rgba(238,242,251,0.95)';
    const borderSub = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(59,130,246,0.12)';
    const txtBody = isDark ? 'rgba(255,255,255,0.85)' : '#1e293b';
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
    const profileMenuRef = useRef(null);
    const filterMenuRef = useRef(null);
    const state = useMemo(() => location.state || {}, [location.state]);
    const {
        orgId, companyName, startYear, endYear,
        mode = 'next_destinations',
        selectedRole = '',
        selectedLocation = '',
        selectedWorkTab = 'best',
        selectedWorkTabId = null,
        selectedCollegeTab = 'best',
        selectedCollegeTabId = null,
        sourceData = null,
        fromInsightsTransition = false,
        showNetworkView = false,
        initialViewMode,
        fromOnboarding = false
    } = state;
    const smoothEntry = Boolean(fromInsightsTransition);
    const pricingNavState = useMemo(() => ({
        from: 'insights_list',
        returnTo: location.pathname,
        returnState: {
            orgId,
            companyName,
            startYear,
            endYear,
            mode,
            selectedRole,
            selectedLocation,
            selectedWorkTab,
            selectedWorkTabId,
            selectedCollegeTab,
            selectedCollegeTabId,
            sourceData,
            fromInsightsTransition,
            showNetworkView,
            initialViewMode,
        },
    }), [
        location.pathname,
        orgId,
        companyName,
        startYear,
        endYear,
        mode,
        selectedRole,
        selectedLocation,
        selectedWorkTab,
        selectedWorkTabId,
        selectedCollegeTab,
        selectedCollegeTabId,
        sourceData,
        fromInsightsTransition,
        showNetworkView,
        initialViewMode,
    ]);
    /* ── side panel state ── */
    const [panelCompany, setPanelCompany] = useState(null);
    const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
    const [roleInput, setRoleInput] = useState('');
    const [searchRoles, setSearchRoles] = useState(() => readSavedRoles(user?.id));
    const [internalData, setInternalData] = useState(state.sourceData);
    const [currentLocation, setCurrentLocation] = useState(state.selectedLocation || '');
    const [isDataLoading, setIsDataLoading] = useState(false);
    const dataCacheRef = useRef(new Map()); // locationKey -> data
    const fetchRetryCountRef = useRef(0);
    useEffect(() => {
        fetchRetryCountRef.current = 0;
    }, [orgId, mode, selectedWorkTab, selectedCollegeTab, startYear, endYear]);
    const [currentCompanyForJobs, setCurrentCompanyForJobs] = useState(null);
    const [currentCompanyForJobsItem, setCurrentCompanyForJobsItem] = useState(null);
    const [hoveredTagIndex, setHoveredTagIndex] = useState(null);
    const resolvedInitialViewMode = useMemo(() => {
        if (initialViewMode) return initialViewMode;
        if (mode === 'work_network' || mode === 'education_network' || mode === 'cross_network') return 'map';
        return 'list';
    }, [initialViewMode, mode]);
    const [listViewMode, setListViewMode] = useState(resolvedInitialViewMode);
    const [selectedCards, setSelectedCards] = useState(new Map()); // Map<id, name>
    const [jobSearchPulse, setJobSearchPulse] = useState(0);
    const [jobSearchMap, setJobSearchMap] = useState(() => readJobSearches(user?.id));
    const [jobSummaryMap, setJobSummaryMap] = useState({});
    const [jobSummaryLoading, setJobSummaryLoading] = useState(false);
    const jobSummaryCacheRef = useRef(new Map());
    const jobSummaryInFlightRef = useRef(new Map());
    const JOB_SUMMARY_TTL_MS = 10 * 60 * 1000; // 10 minutes
    const DATA_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes
    const jobSummaryCleanupRef = useRef(null);
    const [companyFilter, setCompanyFilter] = useState('all'); // all | matches | has_jobs
    const [isCompanyFilterOpen, setIsCompanyFilterOpen] = useState(false);
    const [matchProfileStatus, setMatchProfileStatus] = useState('idle'); // idle | loading | ready | missing | error
    const lastSessionAutoRoleRef = useRef(null);
    const [dismissedNewTags, setDismissedNewTags] = useState(() => readDismissedNewTags(user?.id));
    const [showTrackJobsPopover, setShowTrackJobsPopover] = useState(false);
    const [isExportingAggregated, setIsExportingAggregated] = useState(false);
    const [selectedGlobalAnchor, setSelectedGlobalAnchor] = useState('you');
    const [selectedGlobalTiers, setSelectedGlobalTiers] = useState([]);
    const [globalRoleFilter, setGlobalRoleFilter] = useState('');
    const [isPremiumUser, setIsPremiumUser] = useState(Boolean(user?.is_premium));
    const [isRoleTrackingAll, setIsRoleTrackingAll] = useState(false);
    const getOrgId = useCallback((company) => {
        return company?.organization_id ?? company?.dest_org_id ?? company?.org_id ?? company?.organizationId ?? company?.orgId ?? null;
    }, []);
    const globalCenterLabel = useMemo(() => {
        const raw = String(user?.name || user?.first_name || user?.full_name || user?.email || 'YOU').trim();
        const token = raw.split(' ')[0] || raw;
        return token.slice(0, 24);
    }, [user?.name, user?.first_name, user?.full_name, user?.email]);
    useEffect(() => {
        setJobSearchMap(readJobSearches(user?.id));
        setSearchRoles(readSavedRoles(user?.id));
    }, [user?.id, jobSearchPulse]);
    useEffect(() => {
        setDismissedNewTags(readDismissedNewTags(user?.id));
    }, [user?.id]);
    useEffect(() => {
        setIsPremiumUser(Boolean(user?.is_premium));
    }, [user?.is_premium]);
    useEffect(() => {
        if (authLoading || !user) return;
        getProfile()
            .then((data) => {
                const profileLike = (data?.profile && typeof data.profile === 'object')
                    ? data.profile
                    : (data || {});
                setIsPremiumUser(Boolean(profileLike?.is_premium));
            })
            .catch(() => { /* ignore */ });
    }, [authLoading, user]);
    const dismissNewTag = useCallback((key) => {
        if (!key) return;
        if (dismissedNewTags[key]) return;
        const next = { ...dismissedNewTags, [key]: true };
        setDismissedNewTags(next);
        writeDismissedNewTags(user?.id, next);
    }, [dismissedNewTags, user?.id]);
    useEffect(() => {
        const id = setInterval(() => setJobSearchPulse(Date.now()), 60 * 1000);
        return () => clearInterval(id);
    }, []);
    useEffect(() => {
        if (!isCompanyFilterOpen) return;
        const handleClick = (e) => {
            if (filterMenuRef.current && !filterMenuRef.current.contains(e.target)) {
                setIsCompanyFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [isCompanyFilterOpen]);
    useEffect(() => {
        setListViewMode(resolvedInitialViewMode);
    }, [resolvedInitialViewMode]);
    /* ── scroll lock for role modal ── */
    useEffect(() => {
        if (isRoleModalOpen) {
            const originalStyle = window.getComputedStyle(document.body).overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = originalStyle;
            };
        }
    }, [isRoleModalOpen]);
    const updateSearchRoles = useCallback((next) => {
        const unique = [...new Set(next)].filter(Boolean).slice(0, 10);
        setSearchRoles(unique);
        writeSavedRoles(user?.id, unique);
    }, [user?.id]);
    const toggleCardSelected = (company, index) => {
        if (!showCardSelectionUi) return;
        const id = company.organization_id || company.organization || index;
        const name = String(company.organization || '').trim();
        setSelectedCards((prev) => {
            const next = new Map(prev);
            if (next.has(id)) next.delete(id);
            else next.set(id, name);
            return next;
        });
    };
    const recordJobSearchForCompanies = (companies = []) => {
        const now = Date.now();
        const next = { ...(readJobSearches(user?.id) || {}) };
        (Array.isArray(companies) ? companies : []).forEach((item) => {
            const key = companyKeyFromItem(item);
            if (!key) return;
            next[key] = now;
        });
        writeJobSearches(user?.id, next);
        setJobSearchPulse(now);
    };
    const getJobSearchLabel = (company) => {
        const key = companyKeyFromItem(company);
        if (!key) return '';
        const ts = jobSearchMap[key];
        if (!ts) return '';
        const diff = Date.now() - Number(ts);
        if (!Number.isFinite(diff) || diff < 0) return '';
        return `Job searched ${formatJobSearchAgo(diff)}`;
    };
    // Temporarily hide multi-select UI/action in list cards without removing feature code.
    const showCardSelectionUi = false;
    const clearSelectedCards = () => setSelectedCards(new Map());
    const selectedCount = selectedCards.size;
    const selectedCompanyNames = useMemo(() => {
        const names = [];
        const seen = new Set();
        for (const value of selectedCards.values()) {
            const n = String(value || '').trim();
            const k = n.toLowerCase();
            if (!n || seen.has(k)) continue;
            seen.add(k);
            names.push(n);
        }
        return names;
    }, [selectedCards]);
    const isSelectionMode = selectedCount > 0;
    const selectedPreview = selectedCompanyNames.slice(0, 3).join(', ');
    const selectedOverflow = Math.max(0, selectedCompanyNames.length - 3);
    const selectedSummaryText = selectedPreview
        ? `${selectedPreview}${selectedOverflow > 0 ? ` +${selectedOverflow} more` : ''}`
        : `${selectedCount} selected`;
    useEffect(() => {
        // Auto-add selectedRole from block state if not already in searchRoles
        // We track lastSessionAutoRoleRef to avoid re-adding if they just manually removed it
        if (selectedRole && !searchRoles.includes(selectedRole)) {
            if (lastSessionAutoRoleRef.current !== selectedRole) {
                lastSessionAutoRoleRef.current = selectedRole;
                updateSearchRoles([...searchRoles, selectedRole]);
            }
        }
    }, [selectedRole, searchRoles, updateSearchRoles]);
    useEffect(() => {
        if (mode !== 'location_hiring' || !orgId) return;
        const cacheKey = `${orgId}:${currentLocation || 'all'}:${selectedRole || 'any'}:${startYear || '0'}:${endYear || '0'}`;
        // If "All Locations" and no other filters are active, we already have the data in sourceData
        if (!currentLocation && !selectedRole && !startYear && !endYear) {
            setInternalData(sourceData);
            return;
        }
        const cached = dataCacheRef.current.get(cacheKey);
        const now = Date.now();
        if (cached && cached.data && (now - cached.ts) < DATA_CACHE_TTL_MS) {
            setInternalData(cached.data);
            return;
        }
        if (cached) dataCacheRef.current.delete(cacheKey);
        // Clear internalData when switching between specific cities to avoid stale results.
        // Keep it for "All Locations" (currentLocation === '') to maintain initial summary data.
        if (currentLocation) setInternalData(null);
        setIsDataLoading(true);
        getCompanyInsights(orgId, {
            location: currentLocation || '',
            role: selectedRole || '',
            startDate: startYear ? `${startYear}-01-01` : null,
            endDate: endYear ? `${endYear}-12-31` : null,
            includeCore: false,
            includeNetwork: false,
            includeRole: mode === 'role_hiring',
            includeLocation: mode === 'location_hiring'
        }).then(data => {
            setInternalData(data);
            dataCacheRef.current.set(cacheKey, { data, ts: Date.now() });
        }).catch(err => {
            console.error('Failed to fetch insights for location:', err);
            toast.error('Failed to update location data');
        }).finally(() => {
            setIsDataLoading(false);
        });
    }, [currentLocation, orgId, mode, selectedRole, startYear, endYear, sourceData, DATA_CACHE_TTL_MS]);
    useEffect(() => {
        if (state.sourceData && !internalData) {
            setInternalData(state.sourceData);
        }
    }, [state.sourceData, internalData]);
    const hasSelectedNetworkTabData = useCallback((dataObj) => {
        if (!dataObj || typeof dataObj !== 'object') return false;
        if (mode === 'global_network') {
            const hasWork = dataObj.network_work_tabs && Object.keys(dataObj.network_work_tabs || {}).length > 0;
            const hasEdu = dataObj.network_education_tabs && Object.keys(dataObj.network_education_tabs || {}).length > 0;
            return Boolean(hasWork || hasEdu);
        }
        if (mode === 'work_network') {
            const tabs = dataObj.network_work_tabs && typeof dataObj.network_work_tabs === 'object'
                ? dataObj.network_work_tabs
                : {};
            const key = selectedWorkTab || 'best';
            return Array.isArray(tabs[key]);
        }
        if (mode === 'education_network') {
            const tabs = dataObj.network_education_tabs && typeof dataObj.network_education_tabs === 'object'
                ? dataObj.network_education_tabs
                : {};
            const key = selectedCollegeTab || 'best';
            return Array.isArray(tabs[key]);
        }
        return true;
    }, [mode, selectedWorkTab, selectedCollegeTab]);
    // Fetch base data if missing OR if selected network tab payload is missing in carried sourceData
    useEffect(() => {
        if (!orgId) return;
        const carried = internalData || state.sourceData;
        const needsNetworkRefetch =
            (mode === 'work_network' || mode === 'education_network' || mode === 'global_network') &&
            !hasSelectedNetworkTabData(carried);
        if (carried && !needsNetworkRefetch) return;

        if (fetchRetryCountRef.current >= 5) {
            console.warn("Max retries reached for network data");
            setIsDataLoading(false);
            return;
        }
        fetchRetryCountRef.current += 1;

        setIsDataLoading(true);
        const needsNetworkOnly = mode === 'work_network' || mode === 'education_network' || mode === 'cross_network' || mode === 'global_network';
        const networkMode = mode === 'work_network'
            ? 'work'
            : mode === 'education_network'
                ? 'education'
                : mode === 'cross_network'
                    ? 'cross'
                    : mode === 'global_network'
                        ? 'all'
                        : undefined;
        const networkTab = mode === 'work_network'
            ? (selectedWorkTab || '')
            : mode === 'education_network'
                ? (selectedCollegeTab || '')
                : '';
        const networkTabId = mode === 'work_network'
            ? (selectedWorkTabId != null ? Number(selectedWorkTabId) : undefined)
            : mode === 'education_network'
                ? (selectedCollegeTabId != null ? Number(selectedCollegeTabId) : undefined)
                : undefined;
        getCompanyInsights(orgId, {
            startDate: startYear ? `${startYear}-01-01` : null,
            endDate: endYear ? `${endYear}-12-31` : null,
            includeCore: !needsNetworkOnly,
            includeNetwork: needsNetworkOnly,
            includeRole: mode === 'role_hiring',
            includeLocation: mode === 'location_hiring',
            networkMode,
            networkTab,
            networkTabId,
        }).then(data => {
            setInternalData(data);
        }).catch(err => {
            console.error('Failed to fetch base insights:', err);
        }).finally(() => {
            setIsDataLoading(false);
        });
    }, [orgId, state.sourceData, internalData, startYear, endYear, mode, hasSelectedNetworkTabData, selectedWorkTab, selectedWorkTabId, selectedCollegeTab, selectedCollegeTabId]);
    const meta = MODE_META[mode] || MODE_META.next_destinations;
    const items = meta.getItems(internalData || sourceData, selectedRole, selectedWorkTab, selectedCollegeTab, currentLocation);
    const globalSource = useMemo(() => (internalData || sourceData || {}), [internalData, sourceData]);
    const globalWorkTabs = useMemo(() => (
        (globalSource?.network_work_tabs && typeof globalSource.network_work_tabs === 'object')
            ? globalSource.network_work_tabs
            : {}
    ), [globalSource]);
    const globalEduTabs = useMemo(() => (
        (globalSource?.network_education_tabs && typeof globalSource.network_education_tabs === 'object')
            ? globalSource.network_education_tabs
            : {}
    ), [globalSource]);
    const globalWorkAnchors = Object.keys(globalWorkTabs || {}).filter((k) => k !== 'best');
    const globalEduAnchors = Object.keys(globalEduTabs || {}).filter((k) => k !== 'best');
    const isBestWorkTab = mode === 'work_network' && (selectedWorkTab || 'best') === 'best';
    const isBestCollegeTab = mode === 'education_network' && (selectedCollegeTab || 'best') === 'best';
    const isBestWorkInsufficient = isBestWorkTab && globalWorkAnchors.length < 2;
    const isBestCollegeInsufficient = isBestCollegeTab && globalEduAnchors.length < 2;
    const globalAnchorItems = useMemo(() => {
        if (mode !== 'global_network') return [];
        if (selectedGlobalAnchor === 'you') return [];
        let rows = [];
        let scope = 'work';
        if (selectedGlobalAnchor.startsWith('work:')) {
            const key = selectedGlobalAnchor.slice(5);
            rows = Array.isArray(globalWorkTabs?.[key]) ? globalWorkTabs[key] : [];
        } else if (selectedGlobalAnchor.startsWith('education:')) {
            const key = selectedGlobalAnchor.slice(10);
            rows = Array.isArray(globalEduTabs?.[key]) ? globalEduTabs[key] : [];
            scope = 'education';
        }
        const q = String(globalRoleFilter || '').trim().toLowerCase();
        let filtered = rows;
        if (Array.isArray(selectedGlobalTiers) && selectedGlobalTiers.length > 0) {
            const tierSet = new Set(selectedGlobalTiers);
            filtered = filtered.filter((c) => tierSet.has(classifyGlobalTier(getRelationLabels(c), scope)));
        }
        if (!q) return filtered;
        return filtered.filter((c) => {
            const org = String(c?.organization || '').toLowerCase();
            const rel = String(c?.strongest_relation || '').toLowerCase();
            const tags = Array.isArray(c?.relation_tags)
                ? c.relation_tags.map((t) => typeof t === 'string' ? t : (t?.label || '')).join(' ').toLowerCase()
                : '';
            return org.includes(q) || rel.includes(q) || tags.includes(q);
        });
    }, [mode, selectedGlobalAnchor, globalWorkTabs, globalEduTabs, globalRoleFilter, selectedGlobalTiers]);
    const globalAllDestinationItems = useMemo(() => {
        if (mode !== 'global_network') return [];
        const rows = [];
        Object.entries(globalWorkTabs || {}).forEach(([k, list]) => {
            if (k === 'best' || !Array.isArray(list)) return;
            rows.push(...list);
        });
        Object.entries(globalEduTabs || {}).forEach(([k, list]) => {
            if (k === 'best' || !Array.isArray(list)) return;
            rows.push(...list);
        });
        return rows;
    }, [mode, globalWorkTabs, globalEduTabs]);
    const globalTierCounts = useMemo(() => {
        if (mode !== 'global_network' || selectedGlobalAnchor === 'you') return {};
        let rows = [];
        let scope = 'work';
        if (selectedGlobalAnchor.startsWith('work:')) {
            const key = selectedGlobalAnchor.slice(5);
            rows = Array.isArray(globalWorkTabs?.[key]) ? globalWorkTabs[key] : [];
        } else if (selectedGlobalAnchor.startsWith('education:')) {
            const key = selectedGlobalAnchor.slice(10);
            rows = Array.isArray(globalEduTabs?.[key]) ? globalEduTabs[key] : [];
            scope = 'education';
        }
        const counts = { T1: 0, T2: 0, T3: 0, T4: 0, T5: 0, T6: 0 };
        rows.forEach((c) => {
            const tier = classifyGlobalTier(getRelationLabels(c), scope);
            if (counts[tier] != null) counts[tier] += 1;
        });
        return counts;
    }, [mode, selectedGlobalAnchor, globalWorkTabs, globalEduTabs]);
    const effectiveItems = useMemo(() => {
        if (isBestWorkInsufficient || isBestCollegeInsufficient) return [];
        return mode === 'global_network' ? globalAnchorItems : items;
    }, [mode, globalAnchorItems, items, isBestWorkInsufficient, isBestCollegeInsufficient]);
    const orgIdsForJobs = useMemo(() => {
        const sourceItems = mode === 'global_network' ? globalAllDestinationItems : effectiveItems;
        const seen = new Set();
        const out = [];
        (sourceItems || []).forEach((c) => {
            const raw = getOrgId(c);
            const id = raw == null ? null : Number(raw);
            if (!Number.isFinite(id)) return;
            if (seen.has(id)) return;
            seen.add(id);
            out.push(id);
        });
        // We only render/gate up to 30 rows, so summary beyond that adds latency without UX value.
        return out.slice(0, PREMIUM_COMPANY_LIMIT);
    }, [effectiveItems, getOrgId, mode, globalAllDestinationItems]);
    const orgIdsKey = useMemo(() => orgIdsForJobs.join(','), [orgIdsForJobs]);
    useEffect(() => {
        if (jobSummaryCleanupRef.current) return;
        jobSummaryCleanupRef.current = setInterval(() => {
            const now = Date.now();
            // Cleanup Job Summaries
            const jobCache = jobSummaryCacheRef.current;
            if (jobCache) {
                for (const [key, value] of jobCache.entries()) {
                    if (!value || !value.ts || (now - value.ts) >= JOB_SUMMARY_TTL_MS) {
                        jobCache.delete(key);
                    }
                }
            }
            // Cleanup Data Cache
            const dataCache = dataCacheRef.current;
            if (dataCache) {
                for (const [key, value] of dataCache.entries()) {
                    if (!value || !value.ts || (now - value.ts) >= DATA_CACHE_TTL_MS) {
                        dataCache.delete(key);
                    }
                }
            }
        }, 60 * 1000); // Check every minute
        return () => {
            if (jobSummaryCleanupRef.current) {
                clearInterval(jobSummaryCleanupRef.current);
                jobSummaryCleanupRef.current = null;
            }
        };
    }, [JOB_SUMMARY_TTL_MS, DATA_CACHE_TTL_MS]);
    useEffect(() => {
        if (companyFilter !== 'matches') return;
        if (authLoading) return;
        if (!user) {
            setMatchProfileStatus('missing');
            return;
        }
        setMatchProfileStatus('loading');
        getProfile()
            .then((data) => {
                const profile = (data?.profile && typeof data.profile === 'object')
                    ? data.profile
                    : (data && typeof data === 'object' ? data : {});
                const skills = Array.isArray(profile.skills) ? profile.skills : [];
                const roles = Array.isArray(profile.open_to_roles) ? profile.open_to_roles : [];
                const normalizedSkills = skills
                    .map((s) => (typeof s === 'string' ? s : s?.skill))
                    .map((s) => String(s || '').trim())
                    .filter(Boolean);
                const normalizedRoles = roles
                    .map((r) => (typeof r === 'string' ? r : r?.role))
                    .map((r) => String(r || '').trim())
                    .filter(Boolean);
                setMatchProfileStatus((normalizedSkills.length > 0 || normalizedRoles.length > 0) ? 'ready' : 'missing');
            })
            .catch(() => setMatchProfileStatus('error'));
    }, [companyFilter, authLoading, user]);
    useEffect(() => {
        if (companyFilter === 'all') {
            setJobSummaryLoading(false);
            return;
        }
        if (companyFilter === 'matches') {
            if (matchProfileStatus === 'loading' || matchProfileStatus === 'idle') {
                setJobSummaryLoading(true);
                return;
            }
            if (matchProfileStatus === 'missing' || matchProfileStatus === 'error') {
                setJobSummaryMap({});
                setJobSummaryLoading(false);
                return;
            }
        }
        if (!user || !orgIdsForJobs || orgIdsForJobs.length === 0) {
            setJobSummaryMap({});
            setJobSummaryLoading(false);
            return;
        }
        const now = Date.now();
        const cacheKey = `${orgIdsKey}_${companyFilter === 'matches' ? 'matches' : 'no_matches'}`;
        const cached = jobSummaryCacheRef.current.get(cacheKey);
        if (cached && cached.summary && (now - cached.ts) < JOB_SUMMARY_TTL_MS) {
            setJobSummaryMap(cached.summary);
            setJobSummaryLoading(false);
            return;
        }
        if (cached) jobSummaryCacheRef.current.delete(cacheKey);
        const inFlight = jobSummaryInFlightRef.current.get(cacheKey);
        if (inFlight) {
            setJobSummaryLoading(true);
            inFlight.then((summary) => {
                if (summary) setJobSummaryMap(summary);
            }).finally(() => setJobSummaryLoading(false));
            return;
        }
        setJobSummaryLoading(true);
        const request = getCompanyJobsSummary(
            orgIdsForJobs,
            { includeMatch: companyFilter === 'matches' }
        )
            .then((data) => {
                const summary = data?.summary || {};
                jobSummaryCacheRef.current.set(cacheKey, { summary, ts: Date.now() });
                return summary;
            })
            .catch((err) => {
                if (err && String(err.message || '').includes('429')) {
                    const fallback = jobSummaryCacheRef.current.get(cacheKey);
                    if (fallback && fallback.summary && (Date.now() - fallback.ts) < JOB_SUMMARY_TTL_MS) {
                        return fallback.summary;
                    }
                    return {};
                }
                return {};
            })
            .finally(() => {
                jobSummaryInFlightRef.current.delete(cacheKey);
            });
        jobSummaryInFlightRef.current.set(cacheKey, request);
        request
            .then((summary) => setJobSummaryMap(summary || {}))
            .finally(() => setJobSummaryLoading(false));
    }, [orgIdsKey, user, orgIdsForJobs, JOB_SUMMARY_TTL_MS, companyFilter, matchProfileStatus]);
    const filteredItemsFull = useMemo(() => {
        if (companyFilter === 'all') return effectiveItems;
        return (effectiveItems || []).filter((company) => {
            const key = String(getOrgId(company) || '');
            const summary = jobSummaryMap[key];
            if (!summary) return false;
            if (companyFilter === 'matches') return (summary.match_roles || 0) > 0;
            if (companyFilter === 'has_jobs') return (summary.open_roles || 0) > 0;
            return true;
        });
    }, [effectiveItems, companyFilter, jobSummaryMap, getOrgId]);
    const effectiveCompanyLimit = PREMIUM_COMPANY_LIMIT;
    const filteredItems = useMemo(
        () => (Array.isArray(filteredItemsFull) ? filteredItemsFull.slice(0, effectiveCompanyLimit) : []),
        [filteredItemsFull, effectiveCompanyLimit]
    );
    const hiddenItemsCount = Math.max(0, (filteredItemsFull?.length || 0) - (filteredItems?.length || 0));
    const unlockedRowsCount = useMemo(() => {
        if (isPremiumUser) return filteredItems?.length || 0;
        const total = filteredItems?.length || 0;
        if (companyFilter === 'all') {
            return Math.min(FREE_COMPANY_LIMIT, total);
        }
        // For filtered views, show 50% but never more than free cap.
        return Math.min(FREE_COMPANY_LIMIT, Math.floor(total / 2));
    }, [isPremiumUser, filteredItems, companyFilter]);
    const lockedItemsCount = isPremiumUser ? 0 : Math.max(0, (filteredItems?.length || 0) - unlockedRowsCount);
    const orgIndexById = useMemo(() => {
        const map = new Map();
        (filteredItems || []).forEach((company, idx) => {
            const oid = Number(getOrgId(company));
            if (Number.isFinite(oid) && !map.has(oid)) map.set(oid, idx);
        });
        return map;
    }, [filteredItems, getOrgId]);
    const isLockedCompany = useCallback((company) => {
        if (isPremiumUser) return false;
        const oid = Number(getOrgId(company));
        if (!Number.isFinite(oid)) return false;
        const idx = orgIndexById.get(oid);
        if (idx == null) return false;
        return idx >= unlockedRowsCount;
    }, [isPremiumUser, getOrgId, orgIndexById, unlockedRowsCount]);
    const maskedMapItems = useMemo(() => (
        (filteredItems || []).map((company) => (
            isLockedCompany(company)
                ? { ...company, organization: '••••••', __locked: true }
                : company
        ))
    ), [filteredItems, isLockedCompany]);
    const maskedGlobalWorkTabs = useMemo(() => {
        if (isPremiumUser) return globalWorkTabs;
        const next = {};
        Object.entries(globalWorkTabs || {}).forEach(([k, rows]) => {
            next[k] = (rows || []).map((company) => (
                isLockedCompany(company)
                    ? { ...company, organization: '••••••', __locked: true }
                    : company
            ));
        });
        return next;
    }, [isPremiumUser, globalWorkTabs, isLockedCompany]);
    const maskedGlobalEduTabs = useMemo(() => {
        if (isPremiumUser) return globalEduTabs;
        const next = {};
        Object.entries(globalEduTabs || {}).forEach(([k, rows]) => {
            next[k] = (rows || []).map((company) => (
                isLockedCompany(company)
                    ? { ...company, organization: '••••••', __locked: true }
                    : company
            ));
        });
        return next;
    }, [isPremiumUser, globalEduTabs, isLockedCompany]);
    const allowedGlobalOrgIds = useMemo(() => {
        if (companyFilter === 'all') return null;
        const allowed = new Set();
        Object.entries(jobSummaryMap || {}).forEach(([k, summary]) => {
            if (!summary) return;
            const orgId = Number(k);
            if (!Number.isFinite(orgId)) return;
            if (companyFilter === 'matches' && Number(summary.match_roles || 0) > 0) {
                allowed.add(orgId);
            }
            if (companyFilter === 'has_jobs' && Number(summary.open_roles || 0) > 0) {
                allowed.add(orgId);
            }
        });
        return allowed;
    }, [companyFilter, jobSummaryMap]);
    const isProfileMissingForMatchFilter = companyFilter === 'matches' && matchProfileStatus === 'missing';
    const isCompanyFilterLoading = companyFilter !== 'all' && jobSummaryLoading && !isProfileMissingForMatchFilter;
    const isListLoadingState = isDataLoading || isCompanyFilterLoading;
    const defaultTitle = typeof meta.title === 'function' ? meta.title(companyName || 'Company', selectedRole, selectedWorkTab, selectedCollegeTab, selectedLocation) : meta.title;
    const defaultSubtitle = typeof meta.subtitle === 'function' ? meta.subtitle(selectedRole, selectedWorkTab, selectedCollegeTab, selectedLocation) : meta.subtitle;
    const title = mode === 'global_network'
        ? (selectedGlobalAnchor === 'you'
            ? 'Network Map'
            : selectedGlobalAnchor.startsWith('work:')
                ? `Related Companies for ${selectedGlobalAnchor.slice(5)}`
                : `Related Companies for ${selectedGlobalAnchor.slice(10)}`)
        : defaultTitle;
    const tierLabelMap = selectedGlobalAnchor.startsWith('education:') ? GLOBAL_EDU_TIER_LABELS : GLOBAL_WORK_TIER_LABELS;
    const subtitle = mode === 'global_network'
        ? (selectedGlobalAnchor === 'you'
            ? `Click any company/college node connected to ${globalCenterLabel} to see companies where you can find referrals.`
            : selectedGlobalTiers?.length > 0
                ? `Showing ${selectedGlobalTiers.length === 1 ? (tierLabelMap[selectedGlobalTiers[0]] || selectedGlobalTiers[0]) : `${selectedGlobalTiers.length} tiers`} companies for the selected node.`
                : 'Pick one or more tiers to drill down into relevant companies.')
        : defaultSubtitle;
    useEffect(() => {
        setSelectedGlobalTiers([]);
    }, [selectedGlobalAnchor]);
    /* ── close profile menu clicking outside ── */
    useEffect(() => {
        const fn = (e) => { if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) setIsProfileMenuOpen(false); };
        document.addEventListener('mousedown', fn);
        return () => document.removeEventListener('mousedown', fn);
    }, []);
    const goBack = () => {
        if (fromOnboarding) {
            navigate('/insights', {
                state: {
                    orgId,
                    companyName,
                    startYear,
                    endYear,
                    showNetworkView,
                }
            });
            return;
        }
        navigate(-1);
    };
    const roleTrackEligible = Boolean(
        mode === 'role_hiring' &&
        selectedRole &&
        (internalData?.source_org_in_profile ?? sourceData?.source_org_in_profile) &&
        orgId
    );
    const handleTrackAllRoleCompanies = useCallback(async () => {
        if (!roleTrackEligible) return;
        const roleRows = Array.isArray((internalData || sourceData)?.role_hiring) ? (internalData || sourceData).role_hiring : [];
        const destinationOrgIds = [...new Set(roleRows.map((r) => Number(r?.organization_id)).filter((x) => Number.isFinite(x)))];
        if (!destinationOrgIds.length) {
            toast('No role companies to track yet.');
            return;
        }
        const roleLabel = String(selectedRole || 'role').trim().toLowerCase();
        setIsRoleTrackingAll(true);
        try {
            const res = await captureOrgDestinationMap({
                sourceOrgId: orgId,
                reasonType: `companies hired ${roleLabel} from ${companyName || 'source company'}`,
                mapType: 'role',
                destinationOrgIds,
            });
            if (res?.eligible === false) {
                toast('Tracking unavailable for this source company in your profile.');
            } else if (Number(res?.inserted || 0) > 0) {
                toast.success(`Tracking enabled for ${Number(res.inserted)} rows`);
            } else {
                toast('Already tracked for this role source.');
            }
        } catch (err) {
            toast.error(err?.message || 'Failed to track role companies');
        } finally {
            setIsRoleTrackingAll(false);
        }
    }, [roleTrackEligible, internalData, sourceData, selectedRole, orgId, companyName]);
    const userStorageSuffix = String(user?.id || user?.email || 'anonymous');
    const connectionFiltersStorageKey = `insightsConnectionFiltersState:${userStorageSuffix}`;
    const getStoredConnectionFilters = () => {
        const tryRead = (storageKey) => {
            try {
                const raw = sessionStorage.getItem(storageKey);
                if (!raw) return null;
                const parsed = JSON.parse(raw);
                const applied = parsed?.appliedConnectionFilters;
                return applied && typeof applied === 'object' ? applied : null;
            } catch {
                return null;
            }
        };
        return tryRead(connectionFiltersStorageKey) || tryRead('insightsConnectionFiltersState');
    };
    const buildKeywordsFromRoles = (roles = []) => {
        const tags = roles
            .map((r) => String(r || '').trim())
            .filter(Boolean);
        if (tags.length === 0) return '';
        if (tags.length === 1) return tags[0];
        return tags.join(' OR ');
    };
    const openLinkedInJobsWithAutoRefresh = (url) => {
        // Open directly to the URL for the first "render"
        const win = window.open(url, '_blank', 'noopener,noreferrer');
        if (!win) return;
        // "Double render" by hitting it again/reloading periodically
        // LinkedIn often shows "no jobs" on the first hit but works on second
        [800, 2200, 4500].forEach((delayMs) => {
            window.setTimeout(() => {
                try {
                    if (win.closed) return;
                    // Try setting href again to force a 're-render'
                    win.location.href = url;
                } catch (_) {
                    // Fallback to reload if already on same origin (unlikely for LinkedIn)
                    try { win.location.reload(); } catch (__) { }
                }
            }, delayMs);
        });
    };
    const openLinkedInJobsForSelection = async (roles = []) => {
        const tags = (Array.isArray(roles) ? roles : [])
            .map((r) => String(r || '').trim())
            .filter(Boolean);
        const keywords = buildKeywordsFromRoles(tags);
        // If specific company exists from modal, use it. Otherwise use selected cards.
        const companiesToSearch = currentCompanyForJobs
            ? [currentCompanyForJobs]
            : selectedCompanyNames;
        if (companiesToSearch.length === 0) return;
        try {
            trackFindingJobs({
                source: 'insights_list',
                action: 'open_linkedin',
                selected_company_count: companiesToSearch.length,
                selected_company_names: companiesToSearch.slice(0, 20),
                source_org_id: orgId,
                source_company_name: companyName,
                roles: tags.slice(0, 20),
                keywords: keywords || undefined,
                mode,
            });
            const data = await getLinkedinOrgIdsByCompanyNames(companiesToSearch);
            const ids = Array.isArray(data?.linkedin_org_ids)
                ? data.linkedin_org_ids
                    .map((v) => String(v || '').trim())
                    .filter((v) => /^\d+$/.test(v))
                : [];
            const params = new URLSearchParams();
            if (keywords) params.set('keywords', keywords);
            params.set('f_TPR', 'r2592000');
            if (ids.length > 0) params.set('f_C', ids.join(','));
            const q = params.toString();
            const url = `https://www.linkedin.com/jobs/search-results/${q ? `?${q}` : ''}`;
            const itemsToRecord = currentCompanyForJobsItem
                ? [currentCompanyForJobsItem]
                : Array.from(selectedCards.entries()).map(([id, name]) => ({
                    organization_id: id,
                    organization: name,
                }));
            recordJobSearchForCompanies(itemsToRecord);
            openLinkedInJobsWithAutoRefresh(url);
            setIsRoleModalOpen(false);
            setRoleInput('');
        } catch (err) {
            toast.error(err.message || 'Could not open LinkedIn jobs');
        }
    };
    const handleFindOpenRoles = (company) => {
        const name = typeof company === 'string' ? company : company?.organization;
        const cleanName = String(name || '').trim();
        if (!cleanName) return;
        trackFindingJobs({
            source: 'insights_list',
            action: 'open_modal',
            company_name: cleanName,
            source_org_id: orgId,
            source_company_name: companyName,
            mode,
        });
        setIsRoleModalOpen(false);
        const panelParams = buildPanelParamsForCompany(company);
        recordJobSearchForCompanies([
            company && typeof company === 'object'
                ? company
                : { organization: cleanName }
        ]);
        setPanelCompany({
            ...panelParams,
            initialOuterTab: 'jobs',
        });
    };
    const buildPanelParamsForCompany = (company) => {
        const isRoleMode = mode === 'role_hiring';
        const isWorkMode = mode === 'work_network';
        const isEducationMode = mode === 'education_network';
        const hasRoleModeResults = Array.isArray(sourceData?.role_hiring) && sourceData.role_hiring.length > 0;
        const roleForDetails = (isRoleMode && hasRoleModeResults) ? selectedRole : '';
        const hopForDetails = mode === 'career_paths'
            ? (Number(company?.dominant_hop) === 3 ? 3 : 2)
            : 1;
        const isCrossMode = mode === 'cross_network';
        return {
            sourceOrgId: orgId,
            sourceCompanyName: companyName,
            destOrgId: company?.organization_id,
            companyName: company?.organization,
            hop: hopForDetails,
            startYear,
            endYear,
            role: roleForDetails,
            connectionFilters: getStoredConnectionFilters(),
            crossMatchedEmployeeIds: (isCrossMode || isWorkMode || isEducationMode) && Array.isArray(company?.matched_employee_ids) ? company.matched_employee_ids : [],
            location: (mode === 'location_hiring') ? selectedLocation : '',
            matchedEmployeeId: company?.matched_employee_id,
            matchedEmployeeName: company?.matched_employee_name,
        };
    };
    const openCompanyDetailsForInsight = (company) => {
        const params = buildPanelParamsForCompany(company);
        trackCompanyClicked(params.companyName, {
            source: 'insights_list',
            click_target: 'find_people',
            dest_org_id: params.destOrgId,
            source_org_id: params.sourceOrgId,
            source_company_name: params.sourceCompanyName,
            start_year: params.startYear,
            end_year: params.endYear,
            hop: params.hop,
            role: params.role || undefined,
            location: params.location || undefined,
            mode,
        });
        setPanelCompany(params);
    };
    const openCompanyDetailsInNewTab = (company) => {
        const params = buildPanelParamsForCompany(company);
        const urlParams = new URLSearchParams();
        if (params.sourceOrgId != null) urlParams.set('sourceOrgId', String(params.sourceOrgId));
        if (params.sourceCompanyName) urlParams.set('sourceCompanyName', String(params.sourceCompanyName));
        if (params.destOrgId != null) urlParams.set('destOrgId', String(params.destOrgId));
        if (params.companyName) urlParams.set('companyName', String(params.companyName));
        if (params.hop != null) urlParams.set('hop', String(params.hop));
        if (params.startYear) urlParams.set('startYear', String(params.startYear));
        if (params.endYear) urlParams.set('endYear', String(params.endYear));
        if (params.role) urlParams.set('role', String(params.role));
        if (params.location) urlParams.set('location', String(params.location));
        if (params.connectionFilters && typeof params.connectionFilters === 'object') {
            urlParams.set('cf', JSON.stringify(params.connectionFilters));
        }
        window.open(`/company-details?${urlParams.toString()}`, '_blank', 'noopener,noreferrer');
    };
    if (authLoading) {
        return <div className={appCls} style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: 32, height: 32, border: `3px solid ${accentColor}4d`, borderTopColor: accentColor, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        </div>;
    }
    return (
        <div className={`${appCls} compact-ui`} style={{ minHeight: '100vh', paddingTop: 56, fontFamily: "'Manrope', sans-serif" }}>
            {/* ── HEADER ── */}
            <header className="insights-header" style={{
                position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
                background: bgPage, backdropFilter: 'blur(16px)',
                borderBottom: `1px solid ${borderSub}`,
                padding: '0 24px', height: 56,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between'
            }}>
                <div className="insights-header-left" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <button
                        onClick={goBack}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: 'rgba(255,255,255,0.6)', fontSize: 13
                        }}
                        onMouseEnter={e => e.currentTarget.style.color = '#fff'}
                        onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}
                    >
                        <ArrowLeft size={15} /> Back
                    </button>
                    <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.12)' }} />
                    <div className="insights-header-logo" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} onClick={() => navigate('/')}>
                        <div style={{
                            width: 32, height: 32,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: '#fff', borderRadius: '6px', padding: '4px'
                        }}>
                            <img src="/assets/logo.png" alt="fairhire logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        </div>
                        <span className="insights-header-brand" style={{ fontSize: 16, fontWeight: 800, fontFamily: "'Playfair Display', serif" }}>fairhire</span>
                    </div>
                    {/* mode pill */}
                    <div className="insights-mode-pill" style={{
                        padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                        background: isDark ? '#F59E0B' : '#2563eb',
                        color: isDark ? '#1C1917' : '#fff',
                        letterSpacing: '0.05em'
                    }}>
                        <span>
                            {typeof meta.pillLabel === 'function' ? meta.pillLabel(mode === 'location_hiring' ? selectedLocation : selectedRole).toUpperCase() : mode.replace('_', ' ').toUpperCase()}
                        </span>
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }} ref={profileMenuRef}>
                    <ThemeToggle />
                    <button
                        onClick={() => setIsProfileMenuOpen(v => !v)}
                        style={{
                            width: 36, height: 36, borderRadius: '50%',
                            background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(59,130,246,0.1)',
                            border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(59,130,246,0.2)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
                        }}
                    >
                        <UserIcon size={15} color={isDark ? 'rgba(255,255,255,0.75)' : '#3b82f6'} />
                    </button>
                    <AnimatePresence>
                        {isProfileMenuOpen && (
                            <motion.div
                                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                                style={{
                                    position: 'absolute', right: 0, top: 'calc(100% + 8px)',
                                    width: 200, background: 'rgba(13,16,25,0.98)',
                                    border: '1px solid rgba(255,255,255,0.1)', borderRadius: 14,
                                    padding: 8, boxShadow: '0 16px 48px rgba(0,0,0,0.6)'
                                }}
                            >
                                <div style={{ padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: 4 }}>
                                    <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginBottom: 2 }}>Signed in as</p>
                                    <p style={{ fontSize: 13, fontWeight: 600, color: '#255', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</p>
                                </div>
                                {[
                                    { label: 'Profile', icon: <UserIcon size={14} />, onClick: () => { setIsProfileMenuOpen(false); navigate('/profile'); }, color: 'rgba(255,255,255,0.75)' },
                                    { label: 'Logout', icon: <LogOut size={14} />, onClick: () => { logout(); setIsProfileMenuOpen(false); navigate('/'); toast.success('Logged out'); }, color: '#f87171' },
                                ].map(item => (
                                    <button key={item.label} onClick={item.onClick}
                                        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, background: 'none', border: 'none', color: item.color, fontSize: 13, cursor: 'pointer' }}
                                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                                    >
                                        {item.icon}{item.label}
                                    </button>
                                ))}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </header>
            {/* ── CONTENT ── */}
            {mode === 'location_hiring' ? (
                <LocationHiringView
                    items={items}
                    filteredItems={filteredItems}
                    sourceData={internalData || sourceData}
                    companyName={companyName}
                    selectedLocation={currentLocation}
                    onSelectLocation={(loc) => {
                        setCompanyFilter('all');
                        setCurrentLocation(loc);
                    }}
                    accentColor={accentColor}
                    onCompanyClick={openCompanyDetailsForInsight}
                    onFindRoles={handleFindOpenRoles}
                    selectedCards={selectedCards}
                    toggleCardSelected={toggleCardSelected}
                    getJobSearchLabel={getJobSearchLabel}
                    jobSummaryMap={jobSummaryMap}
                    getOrgId={getOrgId}
                    panelCompany={panelCompany}
                    setPanelCompany={setPanelCompany}
                    orgId={orgId}
                    startYear={startYear}
                    endYear={endYear}
                    mode={mode}
                    isLoading={isDataLoading}
                    companyFilter={companyFilter}
                    setCompanyFilter={setCompanyFilter}
                    isCompanyFilterOpen={isCompanyFilterOpen}
                    setIsCompanyFilterOpen={setIsCompanyFilterOpen}
                    jobSummaryLoading={jobSummaryLoading}
                    filterMenuRef={filterMenuRef}
                />
            ) : (
                <motion.div
                    initial={smoothEntry ? { opacity: 0, y: 8 } : false}
                    animate={smoothEntry ? { opacity: 1, y: 0 } : false}
                    transition={smoothEntry ? { duration: 0.3, ease: 'easeOut' } : undefined}
                    className="insights-content"
                    style={{
                        width: '100%',
                        padding: '32px 0 0',
                        height: 'calc(100vh - 72px)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    {/* headline */}
                    <div style={{ maxWidth: INSIGHTS_LIST_CONTENT_MAX_WIDTH, width: '100%', margin: '0 auto 20px', padding: '0 24px' }}>
                        <h1 style={{
                            fontSize: 'clamp(20px, 2.4vw, 32px)', fontWeight: 800,
                            fontFamily: "'Playfair Display', serif", lineHeight: 1.15, marginBottom: 10
                        }}>
                            {title}
                        </h1>
                        <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.45)', lineHeight: 1.6 }}>{subtitle}</p>
                        <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'stretch' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
                                {/* Filter Group (Button + Subtext) */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: '1 1 300px' }}>
                                    <div ref={filterMenuRef} className="insights-filter" style={{ position: 'relative', width: 'fit-content' }}>
                                        <button
                                            onClick={() => {
                                                setIsCompanyFilterOpen((v) => !v);
                                            }}
                                            className="insights-filter-button"
                                            style={{
                                                display: 'inline-flex', alignItems: 'center', gap: 10,
                                                padding: '8px 12px', borderRadius: 10, fontSize: 12, fontWeight: 700,
                                                background: isDark ? `${accentColor}15` : `${accentColor}08`,
                                                border: `1.5px solid ${accentColor}`,
                                                color: isDark ? '#fff' : '#1e293b',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            <SlidersHorizontal size={14} color={isCompanyFilterLoading ? 'rgba(255,255,255,0.5)' : accentColor} />
                                            <span>
                                                {companyFilter === 'all' ? 'All companies' : companyFilter === 'matches' ? 'Roles matching your profile' : 'Has open roles'}
                                            </span>
                                            {!isCompanyFilterLoading && (
                                                <span style={{
                                                    background: accentColor,
                                                    color: '#fff',
                                                    padding: '2px 7px',
                                                    borderRadius: 5,
                                                    fontSize: 10,
                                                    fontWeight: 800,
                                                    minWidth: 24,
                                                    textAlign: 'center'
                                                }}>
                                                    {filteredItems?.length || 0}
                                                </span>
                                            )}
                                            {!dismissedNewTags.all_companies_filter && (
                                                <FeatureNewBadge
                                                    isDark={isDark}
                                                    tooltip="Filter companies based on job openings and matches."
                                                />
                                            )}
                                            <ChevronDown size={15} color={isCompanyFilterLoading ? 'rgba(255,255,255,0.5)' : accentColor} style={{
                                                transform: isCompanyFilterOpen ? 'rotate(180deg)' : 'none',
                                                transition: 'transform 0.2s',
                                            }} />
                                        </button>
                                        {isCompanyFilterOpen && (
                                            <div className="insights-filter-menu" style={{
                                                position: 'absolute', top: 'calc(100% + 8px)', left: 0, zIndex: 120,
                                                width: 260, padding: 10, borderRadius: 12,
                                                background: 'rgba(13,16,25,0.98)', border: '1px solid rgba(255,255,255,0.12)',
                                                boxShadow: '0 18px 48px rgba(0,0,0,0.6)',
                                            }}>
                                                <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', color: 'rgba(255,255,255,0.35)', marginBottom: 8 }}>
                                                    SHOW COMPANIES WHERE
                                                </div>
                                                {[
                                                    { key: 'all', label: 'All companies', sub: 'Show the full list' },
                                                    { key: 'matches', label: 'Roles matching your profile', sub: 'Jobs that fit your background' },
                                                    { key: 'has_jobs', label: 'Has open roles', sub: 'Any active job listings' },
                                                ].map((opt) => (
                                                    <button
                                                        key={opt.key}
                                                        onClick={() => {
                                                            dismissNewTag('all_companies_filter');
                                                            setCompanyFilter(opt.key);
                                                            setIsCompanyFilterOpen(false);
                                                        }}
                                                        style={{
                                                            width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: 10,
                                                            background: companyFilter === opt.key ? 'rgba(245,158,11,0.1)' : 'transparent',
                                                            border: companyFilter === opt.key ? '1px solid rgba(245,158,11,0.25)' : '1px solid transparent',
                                                            color: companyFilter === opt.key ? accentColor : 'rgba(255,255,255,0.8)',
                                                            cursor: 'pointer', marginBottom: 6
                                                        }}
                                                    >
                                                        <div style={{ fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            <span>{opt.label}</span>
                                                        </div>
                                                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>{opt.sub}</div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginLeft: 2 }}>
                                        Filter by open roles or jobs matching your profile
                                    </span>
                                </div>
                                {/* Right Group (Toggles + Track Jobs) */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                    {(mode === 'work_network' || mode === 'education_network' || mode === 'cross_network') && filteredItems.length > 0 && (
                                        <div style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            padding: 2,
                                            borderRadius: 999,
                                            background: isDark ? 'rgba(255,255,255,0.06)' : '#F8FAFC',
                                            border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid #E2E8F0',
                                            gap: 2,
                                        }}>
                                            {[{ key: 'list', label: 'List' }, { key: 'map', label: 'Map' }].map((v) => {
                                                const isActive = listViewMode === v.key;
                                                return (
                                                    <button
                                                        key={v.key}
                                                        onClick={() => setListViewMode(v.key)}
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            height: 30,
                                                            padding: '0 12px',
                                                            borderRadius: 999,
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            fontSize: 11,
                                                            fontWeight: 700,
                                                            background: isActive
                                                                ? (isDark ? 'rgba(255,255,255,0.16)' : '#FFFFFF')
                                                                : 'transparent',
                                                            color: isActive
                                                                ? (isDark ? '#F8FAFC' : '#0F172A')
                                                                : (isDark ? 'rgba(255,255,255,0.62)' : '#64748B'),
                                                            boxShadow: isActive
                                                                ? (isDark ? '0 1px 2px rgba(0,0,0,0.22)' : '0 1px 2px rgba(15,23,42,0.08)')
                                                                : 'none',
                                                            transition: 'all 0.2s ease',
                                                        }}
                                                    >
                                                        {v.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                    {roleTrackEligible && (
                                        <div
                                            style={{ position: 'relative' }}
                                            onMouseEnter={() => setShowTrackJobsPopover(true)}
                                            onMouseLeave={() => setShowTrackJobsPopover(false)}
                                        >
                                            <AnimatePresence>
                                                {showTrackJobsPopover && (
                                                    <motion.div
                                                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                                        style={{
                                                            position: 'absolute',
                                                            top: 'calc(100% + 12px)',
                                                            right: 0,
                                                            width: 300,
                                                            maxWidth: 'calc(100vw - 48px)',
                                                            zIndex: 150,
                                                            background: isDark ? '#111827' : '#ffffff',
                                                            border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)',
                                                            borderRadius: 16,
                                                            padding: '16px 20px',
                                                            boxShadow: isDark ? '0 25px 60px rgba(0,0,0,0.6)' : '0 20px 40px rgba(15,23,42,0.15)',
                                                            pointerEvents: 'none',
                                                            textAlign: 'left',
                                                            fontFamily: "'Manrope', sans-serif"
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                            <h3 style={{ fontSize: 14, fontWeight: 800, color: isDark ? '#fff' : '#0F172A', margin: 0, lineHeight: 1.3 }}>
                                                                Get notified about new openings
                                                            </h3>
                                                            <p style={{ fontSize: 11.5, color: isDark ? 'rgba(255,255,255,0.45)' : '#64748b', lineHeight: 1.5, margin: 0, fontWeight: 500 }}>
                                                                We'll track top {filteredItems.length} companies where {companyName} {selectedRole || 'engineers'} landed and alert you the moment a {selectedRole || 'matching'} role goes live.
                                                            </p>
                                                        </div>
                                                        {/* Pointer (Top) */}
                                                        <div style={{
                                                            position: 'absolute', bottom: '100%', right: 30,
                                                            width: 0, height: 0,
                                                            borderLeft: '8px solid transparent',
                                                            borderRight: '8px solid transparent',
                                                            borderBottom: `8px solid ${isDark ? '#111827' : '#fff'}`,
                                                            filter: 'drop-shadow(0 -2px 2px rgba(0,0,0,0.05))'
                                                        }} />
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                            <button
                                                onClick={handleTrackAllRoleCompanies}
                                                disabled={isRoleTrackingAll}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 8,
                                                    padding: '8px 12px',
                                                    borderRadius: 10,
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                    border: isDark ? '1px solid rgba(59,130,246,0.35)' : '1px solid rgba(37,99,235,0.3)',
                                                    background: isDark ? 'rgba(59,130,246,0.15)' : 'rgba(37,99,235,0.08)',
                                                    color: isDark ? '#93C5FD' : '#1d4ed8',
                                                    cursor: isRoleTrackingAll ? 'default' : 'pointer',
                                                    opacity: isRoleTrackingAll ? 0.8 : 1,
                                                }}
                                            >
                                                {isRoleTrackingAll ? <Loader2 size={13} className="animate-spin" /> : <Bell size={13} fill="currentColor" />}
                                                Track jobs in these companies
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                        {mode === 'global_network' && (
                            <div style={{ maxWidth: INSIGHTS_GLOBAL_NETWORK_MAX_WIDTH, width: '100%', margin: '0 auto', padding: '0 24px 22px' }}>
                                <ProfileGlobalMapView
                                    workAnchors={globalWorkAnchors}
                                    educationAnchors={globalEduAnchors}
                                    centerLabel={globalCenterLabel}
                                    selectedAnchor={selectedGlobalAnchor}
                                    setSelectedAnchor={setSelectedGlobalAnchor}
                                    selectedTiers={selectedGlobalTiers}
                                    setSelectedTiers={setSelectedGlobalTiers}
                                    workTabsData={maskedGlobalWorkTabs}
                                    collegeTabsData={maskedGlobalEduTabs}
                                    allowedOrgIds={allowedGlobalOrgIds}
                                    isRoleFilterLoading={isCompanyFilterLoading}
                                    onDestinationAction={(company, action) => {
                                        if (isLockedCompany(company)) {
                                            navigate('/pricing', { state: { ...pricingNavState, from: action === 'roles' ? 'find_open_roles_locked_map' : 'find_my_contact_locked_map' } });
                                            return;
                                        }
                                        if (action === 'roles') return handleFindOpenRoles(company);
                                        return openCompanyDetailsForInsight(company);
                                    }}
                                />
                            </div>
                        )}
                        {/* Network Map View */}
                        {mode !== 'global_network' && (
                            ((mode === 'work_network' || mode === 'education_network' || mode === 'cross_network') && listViewMode === 'map' && filteredItems.length > 0 ? (
                                <div style={{ maxWidth: INSIGHTS_LIST_CONTENT_MAX_WIDTH, width: '100%', margin: '0 auto', padding: '0 24px 36px' }}>
                                    <NetworkMapView
                                        centerLabel={
                                            mode === 'work_network'
                                                ? (selectedWorkTab !== 'best' ? selectedWorkTab : (companyName || 'Work'))
                                                : mode === 'education_network'
                                                    ? (selectedCollegeTab !== 'best' ? selectedCollegeTab : (companyName || 'Education'))
                                                    : (companyName || 'Cross Network')
                                        }
                                        centerType={mode === 'education_network' ? 'education' : 'work'}
                                        useTagTiers={
                                            (mode === 'work_network' && (selectedWorkTab || 'best') === 'best')
                                            || (mode === 'education_network' && (selectedCollegeTab || 'best') === 'best')
                                            || mode === 'cross_network'
                                        }
                                        tagTierMode={mode === 'cross_network' ? 'combo' : 'single'}
                                        tagTierContext={
                                            mode === 'cross_network'
                                                ? 'cross_network'
                                                : (mode === 'work_network' && (selectedWorkTab || 'best') === 'best')
                                                    ? 'best_work'
                                                    : (mode === 'education_network' && (selectedCollegeTab || 'best') === 'best')
                                                        ? 'best_education'
                                                        : 'default'
                                        }
                                        companies={maskedMapItems}
                                        orgId={orgId}
                                        companyName={companyName}
                                        onCompanyClick={(company, action) => {
                                            if (isLockedCompany(company)) {
                                                navigate('/pricing', { state: { ...pricingNavState, from: action === 'roles' ? 'find_open_roles_locked_map' : 'find_my_contact_locked_map' } });
                                                return;
                                            }
                                            if (action === 'roles') handleFindOpenRoles(company);
                                            else openCompanyDetailsForInsight(company);
                                        }}
                                        style={{ minHeight: 520 }}
                                    />
                                </div>
                            ) : (
                                <div style={{ maxWidth: INSIGHTS_LIST_CONTENT_MAX_WIDTH, width: '100%', margin: '0 auto', padding: '0 24px 36px' }}>
                                    {filteredItems.length === 0 ? (
                                        <motion.div
                                            initial={{ opacity: 0, y: 16 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.4 }}
                                            style={{
                                                display: 'flex', flexDirection: 'column', alignItems: 'center',
                                                padding: '64px 32px', textAlign: 'center',
                                                background: 'rgba(255,255,255,0.025)',
                                                border: '1px solid rgba(255,255,255,0.07)',
                                                borderRadius: 24,
                                            }}
                                        >
                                            {/* Animated icon container */}
                                            <div style={{
                                                width: 72, height: 72, borderRadius: 20,
                                                background: 'rgba(255,255,255,0.05)',
                                                border: '1px solid rgba(255,255,255,0.1)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                marginBottom: 24, position: 'relative',
                                            }}>
                                                {isListLoadingState ? (
                                                    <Loader2 size={28} className="animate-spin" style={{ color: accentColor }} />
                                                ) : (
                                                    <Inbox size={30} color={isDark ? "rgba(255,255,255,0.25)" : "#94a3b8"} />
                                                )}
                                                {/* decorative ring */}
                                                <div style={{
                                                    position: 'absolute', inset: -8,
                                                    borderRadius: 26, border: '1px dashed rgba(255,255,255,0.07)',
                                                    pointerEvents: 'none',
                                                }} />
                                            </div>
                                            <h3 style={{
                                                fontSize: 18, fontWeight: 800, color: isDark ? 'rgba(255,255,255,0.7)' : '#1e293b',
                                                fontFamily: "'Playfair Display', serif", marginBottom: 10, lineHeight: 1.3
                                            }}>
                                                {(isBestWorkInsufficient || isBestCollegeInsufficient)
                                                    ? (isBestWorkInsufficient
                                                        ? 'Add multiple work experiences to see Best Picks'
                                                        : 'Add multiple colleges to see Best Picks')
                                                    : mode === 'global_network' && selectedGlobalAnchor === 'you'
                                                        ? 'Select a company or college node'
                                                        : isListLoadingState
                                                            ? 'Filtering companies…'
                                                            : isProfileMissingForMatchFilter
                                                                ? 'Complete your profile to see relevant jobs'
                                                                : 'No data available yet'}
                                            </h3>
                                            <p style={{
                                                fontSize: 13, color: isDark ? 'rgba(255,255,255,0.35)' : '#64748b', lineHeight: 1.7,
                                                maxWidth: 380, marginBottom: 28
                                            }}>
                                                {(isBestWorkInsufficient || isBestCollegeInsufficient)
                                                    ? (isBestWorkInsufficient
                                                        ? 'Best work picks need at least two work anchors. Add multiple workplaces in your profile to unlock this view.'
                                                        : 'Best college picks need at least two college anchors. Add multiple colleges in your profile to unlock this view.')
                                                    : mode === 'global_network' && selectedGlobalAnchor === 'you'
                                                        ? `Start from the map above. Click any work or education node connected to ${globalCenterLabel} to load relevant companies here.`
                                                        : isListLoadingState
                                                            ? 'Hang tight, we are checking job signals and matching roles for your filter.'
                                                            : isProfileMissingForMatchFilter
                                                                ? 'Add skills to your profile to see jobs relevant to your profile.'
                                                                : `No results for this filter yet. Try adjusting your criteria or check back soon.`}
                                            </p>
                                            {!isListLoadingState && (
                                                <>
                                                    {/* Divider */}
                                                    <div style={{
                                                        width: 40, height: 1, background: 'rgba(255,255,255,0.1)',
                                                        marginBottom: 22,
                                                    }} />
                                                    {/* Tip */}
                                                    <div style={{
                                                        display: 'flex', alignItems: 'flex-start', gap: 10,
                                                        background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)',
                                                        borderRadius: 12, padding: '12px 16px', maxWidth: 380, textAlign: 'left'
                                                    }}>
                                                        <AlertCircle size={14} color="#F59E0B" style={{ flexShrink: 0, marginTop: 2 }} />
                                                        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', lineHeight: 1.6, margin: 0 }}>
                                                            Try going back and exploring a different insight category — other views may have richer data for this company.
                                                        </p>
                                                    </div>
                                                </>
                                            )}
                                            {!isListLoadingState && (
                                                <button
                                                    onClick={() => navigate(-1)}
                                                    style={{
                                                        marginTop: 28, padding: '10px 22px', borderRadius: 10,
                                                        background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)',
                                                        color: 'rgba(255,255,255,0.65)', fontSize: 13, fontWeight: 700,
                                                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                                                        transition: 'all 0.15s'
                                                    }}
                                                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; e.currentTarget.style.color = '#fff'; }}
                                                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.color = 'rgba(255,255,255,0.65)'; }}
                                                >
                                                    <ArrowLeft size={13} /> Go back
                                                </button>
                                            )}
                                            {isProfileMissingForMatchFilter && (
                                                <button
                                                    onClick={() => navigate('/profile')}
                                                    style={{
                                                        marginTop: 12,
                                                        padding: '10px 22px',
                                                        borderRadius: 10,
                                                        background: isDark
                                                            ? 'linear-gradient(135deg, #F59E0B, #D97706)'
                                                            : 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                                                        border: 'none',
                                                        color: isDark ? '#1C1917' : '#ffffff',
                                                        fontSize: 13,
                                                        fontWeight: 700,
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 6,
                                                    }}
                                                >
                                                    <PlusCircle size={13} /> Add Profile Details
                                                </button>
                                            )}
                                        </motion.div>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                            {filteredItems.map((company, idx) => {
                                                const companyId = company.organization_id || company.organization || idx;
                                                const isLockedRow = !isPremiumUser && idx >= unlockedRowsCount;
                                                const isCardSelected = selectedCards.has(companyId);
                                                const col = avatarColor(company.organization);
                                                const subtext = meta.getSubtext(company, companyName || 'Company', selectedRole, selectedWorkTab, selectedCollegeTab, selectedLocation);
                                                const tags = typeof meta.getTags === 'function' ? (meta.getTags(company, companyName || 'Company', selectedRole, selectedWorkTab, selectedCollegeTab, selectedLocation) || []) : [];
                                                const rightLabel = meta.getRightLabel(company);
                                                const rightColor = meta.getRightColor(company);
                                                const jobSearchLabel = getJobSearchLabel(company);
                                                const summary = jobSummaryMap[String(getOrgId(company) || '')];
                                                const matchCount = summary?.match_roles || 0;
                                                const openCount = summary?.open_roles || 0;
                                                return (
                                                    <motion.div
                                                        key={company.organization_id || idx}
                                                        initial={{ opacity: 0, y: smoothEntry ? 6 : 12 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{
                                                            duration: smoothEntry ? 0.22 : 0.35,
                                                            delay: smoothEntry ? Math.min(idx * 0.015, 0.12) : idx * 0.04
                                                        }}
                                                        className="insights-list-item"
                                                        style={{
                                                            background: isCardSelected ? 'rgba(245,158,11,0.04)' : 'rgba(255,255,255,0.04)',
                                                            border: isCardSelected ? '1px solid rgba(245,158,11,0.3)' : '1px solid rgba(255,255,255,0.09)',
                                                            borderRadius: 14, padding: '16px 18px',
                                                            display: 'flex', alignItems: 'center', gap: showCardSelectionUi ? 18 : 14,
                                                            position: 'relative',
                                                            transition: 'all 0.2s',
                                                            cursor: isLockedRow ? 'not-allowed' : 'pointer',
                                                            boxShadow: isCardSelected ? '0 4px 16px rgba(245,158,11,0.1)' : 'none',
                                                            filter: 'none',
                                                            opacity: 1,
                                                        }}
                                                        onClick={(e) => {
                                                            if (isLockedRow) {
                                                                navigate('/pricing', { state: { ...pricingNavState, from: 'company_list_locked_row' } });
                                                                return;
                                                            }
                                                            const target = e.target instanceof Element ? e.target : null;
                                                            if (target && (target.closest('button') || target.closest('a'))) return;
                                                            if (showCardSelectionUi) {
                                                                toggleCardSelected(company, idx);
                                                            }
                                                            trackInsightBlockClicked({
                                                                source: 'insights_list',
                                                                mode,
                                                                list_index: idx + 1,
                                                                company_name: company.organization,
                                                                dest_org_id: company.organization_id,
                                                                source_org_id: orgId,
                                                                source_company_name: companyName,
                                                                start_year: startYear,
                                                                end_year: endYear, selected_role: selectedRole || undefined,
                                                                selected_location: selectedLocation || undefined,
                                                                selected_work_tab: selectedWorkTab || undefined,
                                                                selected_college_tab: selectedCollegeTab || undefined,
                                                                is_multi_select: true
                                                            });
                                                        }}
                                                        onMouseEnter={e => {
                                                            if (!isCardSelected && !isLockedRow) {
                                                                e.currentTarget.style.borderColor = 'rgba(245,158,11,0.25)';
                                                                e.currentTarget.style.boxShadow = '0 4px 24px rgba(0,0,0,0.4)';
                                                            }
                                                        }}
                                                        onMouseLeave={e => {
                                                            if (!isCardSelected && !isLockedRow) {
                                                                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.09)';
                                                                e.currentTarget.style.boxShadow = 'none';
                                                            }
                                                        }}
                                                    >
                                                        {jobSearchLabel && (
                                                            <div
                                                                style={{
                                                                    position: 'absolute',
                                                                    top: 10,
                                                                    right: 0,
                                                                    transform: 'translateY(-50%)',
                                                                    padding: '3px 10px',
                                                                    borderRadius: '0px 12px 0px 12px',
                                                                    fontSize: 10,
                                                                    fontWeight: 700,
                                                                    background: isDark ? 'rgba(52, 211, 153, 0.15)' : 'rgba(16, 185, 129, 0.08)',
                                                                    color: isDark ? '#34D399' : '#059669',
                                                                    border: `1px solid ${isDark ? 'rgba(52, 211, 153, 0.25)' : 'rgba(16, 185, 129, 0.2)'}`,
                                                                    letterSpacing: '0.02em',
                                                                }}
                                                            >
                                                                {jobSearchLabel}
                                                            </div>
                                                        )}
                                                        {/* multi-select indicator (hidden for now, keep code for quick rollback) */}
                                                        {showCardSelectionUi && (
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20, flexShrink: 0 }}>
                                                                {isCardSelected ? (
                                                                    <CheckCircle2 size={20} color={accentColor} fill={isDark ? "rgba(245,158,11,0.2)" : "rgba(37,99,235,0.2)"} />
                                                                ) : (
                                                                    <Circle size={20} color={isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)"} />
                                                                )}
                                                            </div>
                                                        )}
                                                        {/* avatar */}
                                                        <div className="insights-avatar" style={{
                                                            width: 44, height: 44, borderRadius: 12, background: `${col}20`,
                                                            border: `1.5px solid ${col}40`, display: 'flex', alignItems: 'center',
                                                            justifyContent: 'center', fontSize: 18, fontWeight: 800, color: col, flexShrink: 0
                                                        }}>
                                                            {company.organization[0]?.toUpperCase() || '?'}
                                                        </div>
                                                        {/* text */}
                                                        <div className="insights-text" style={{ flex: 1, minWidth: 0 }}>
                                                            <p className="insights-title" style={{
                                                                fontSize: 16,
                                                                fontWeight: 700,
                                                                color: isDark ? '#fff' : '#1e293b',
                                                                marginBottom: 4,
                                                                filter: isLockedRow ? 'blur(7px)' : 'none',
                                                                userSelect: isLockedRow ? 'none' : 'auto',
                                                            }}>{company.organization}</p>
                                                            <p className="insights-subtext" style={{ fontSize: 12, color: isDark ? 'rgba(255,255,255,0.4)' : '#64748b', lineHeight: 1.5, whiteSpace: 'pre-line' }}>
                                                                {subtext}
                                                            </p>
                                                            {tags.length > 0 && (() => {
                                                                const allTagRows = tags.map((tag) => ({
                                                                    label: typeof tag === 'string' ? String(tag || '') : String(tag?.label || ''),
                                                                    detail: typeof tag === 'string' ? '' : String(tag?.detail || ''),
                                                                })).filter((t) => t.label);
                                                                const technicalLabels = [
                                                                    'Best person background', 'Work details', 'College details',
                                                                    'Roles', 'Tenure', 'Branches', 'Batches',
                                                                ];
                                                                const tagRows = allTagRows
                                                                    .filter(t => (
                                                                        !technicalLabels.includes(t.label) &&
                                                                        !t.label.startsWith('Work: ') &&
                                                                        !t.label.startsWith('College: ') &&
                                                                        !t.label.startsWith('Matched workplaces: ') &&
                                                                        !t.label.startsWith('Matched colleges: ')
                                                                    ))
                                                                    .slice(0, 3);
                                                                const isHovered = hoveredTagIndex === idx;
                                                                const hasBestWork = allTagRows.some((t) =>
                                                                    t.label.startsWith('Matched workplaces:')
                                                                    || t.label.startsWith('Work:')
                                                                    || t.label === 'Roles'
                                                                    || t.label === 'Tenure'
                                                                    || t.label === 'Work details'
                                                                );
                                                                const hasBestCollege = allTagRows.some((t) => t.label.startsWith('Matched colleges:') || t.label.startsWith('College:'));
                                                                const isIndividualWork = !hasBestWork && !hasBestCollege && allTagRows[0]?.label === 'Same company';
                                                                const isIndividualCollege = !hasBestWork && !hasBestCollege && allTagRows[0]?.label === 'Same college';
                                                                const buildBestMatchLines = (roleLabel, tenureLabel, detailLabel) => {
                                                                    const detailTag = allTagRows.find((t) => t.label === detailLabel);
                                                                    if (detailTag?.detail) {
                                                                        return String(detailTag.detail)
                                                                            .split('|')
                                                                            .map((s) => s.trim())
                                                                            .filter(Boolean)
                                                                            .map((line) => {
                                                                                const parts = line.split('·').map((p) => p.trim());
                                                                                return {
                                                                                    name: parts[0] || '',
                                                                                    role: parts[1] || '',
                                                                                    tenure: parts[2] || '',
                                                                                };
                                                                            })
                                                                            .filter((row) => row.name);
                                                                    }
                                                                    const roleOrBranch = allTagRows.find((t) => t.label === roleLabel);
                                                                    const tenureOrBatch = allTagRows.find((t) => t.label === tenureLabel);
                                                                    const splitPairs = (detail) =>
                                                                        String(detail || '')
                                                                            .split('·')
                                                                            .map((s) => s.trim())
                                                                            .filter(Boolean);
                                                                    const roleParts = splitPairs(roleOrBranch?.detail);
                                                                    const tenureParts = splitPairs(tenureOrBatch?.detail);
                                                                    const map = new Map();
                                                                    roleParts.forEach((p) => {
                                                                        const [name, rest] = p.split(':').map((s) => s.trim());
                                                                        if (!name) return;
                                                                        map.set(name, { name, role: rest || '' });
                                                                    });
                                                                    tenureParts.forEach((p) => {
                                                                        const [name, rest] = p.split(':').map((s) => s.trim());
                                                                        if (!name) return;
                                                                        const prev = map.get(name) || { name, role: '' };
                                                                        map.set(name, { ...prev, tenure: rest || '' });
                                                                    });
                                                                    return Array.from(map.values());
                                                                };
                                                                const bestWorkLines = hasBestWork ? buildBestMatchLines('Roles', 'Tenure', 'Work details') : null;
                                                                const bestCollegeLines = hasBestCollege ? buildBestMatchLines('Branches', 'Batches', 'College details') : null;
                                                                const buildIndividualLine = () => {
                                                                    if (!isIndividualWork && !isIndividualCollege) return null;
                                                                    if (isIndividualWork) {
                                                                        const roleTag = allTagRows.find((t) => /role/i.test(t.label));
                                                                        const tenureTag = allTagRows.find((t) => /tenure/i.test(t.label));
                                                                        const detail = roleTag?.detail || '';
                                                                        const match = detail.match(/Employee role:\s*(.*)/i);
                                                                        const empRole = match ? match[1] : '';
                                                                        const roleLine = empRole
                                                                            ? empRole
                                                                            : '';
                                                                        return [{
                                                                            name: selectedWorkTab || 'Same company',
                                                                            role: roleLine,
                                                                            tenure: tenureTag?.label || '',
                                                                        }];
                                                                    }
                                                                    const branchTag = allTagRows.find((t) => /branch/i.test(t.label));
                                                                    const batchTag = allTagRows.find((t) => /batch/i.test(t.label));
                                                                    const detail = branchTag?.detail || '';
                                                                    const match = detail.match(/Employee branch:\s*(.*)/i);
                                                                    const empBranch = match ? match[1] : '';
                                                                    const branchLine = empBranch
                                                                        ? empBranch
                                                                        : '';
                                                                    return [{
                                                                        name: selectedCollegeTab || 'Same college',
                                                                        role: branchLine,
                                                                        tenure: batchTag?.label || '',
                                                                    }];
                                                                };
                                                                const individualLines = buildIndividualLine();
                                                                return (
                                                                    <div
                                                                        className="insights-tags"
                                                                        onMouseEnter={() => setHoveredTagIndex(idx)}
                                                                        onMouseLeave={() => setHoveredTagIndex(null)}
                                                                        style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', position: 'relative' }}
                                                                    >
                                                                        {tagRows.map((t, i) => (
                                                                            <span
                                                                                key={`${t.label}-${i}`}
                                                                                style={{
                                                                                    padding: '3px 9px',
                                                                                    borderRadius: 999,
                                                                                    fontSize: 10,
                                                                                    fontWeight: 700,
                                                                                    color: isDark
                                                                                        ? (isHovered ? '#FFD27A' : '#F59E0B')
                                                                                        : (isHovered ? '#1e40af' : '#3b5ea8'),
                                                                                    border: isDark
                                                                                        ? (isHovered ? '1px solid rgba(245,158,11,0.65)' : '1px solid rgba(245,158,11,0.4)')
                                                                                        : (isHovered ? '1px solid #93c5fd' : '1px solid #bfdbfe'),
                                                                                    background: isDark
                                                                                        ? (isHovered ? 'rgba(245,158,11,0.2)' : 'rgba(245,158,11,0.1)')
                                                                                        : (isHovered ? '#dbeafe' : '#eff6ff'),
                                                                                    letterSpacing: '0.01em',
                                                                                    transition: 'all 0.15s',
                                                                                }}
                                                                            >
                                                                                {t.label.length > 20 ? t.label.substring(0, 20) + '...' : t.label}
                                                                            </span>
                                                                        ))}
                                                                        {isHovered && (
                                                                            <div
                                                                                style={{
                                                                                    position: 'absolute',
                                                                                    left: 0,
                                                                                    top: '100%',
                                                                                    marginTop: 8,
                                                                                    padding: '10px 12px',
                                                                                    borderRadius: 10,
                                                                                    background: isDark ? 'rgba(15, 23, 42, 0.98)' : '#ffffff',
                                                                                    border: isDark ? '1px solid rgba(245,158,11,0.35)' : '1px solid #bfdbfe',
                                                                                    boxShadow: isDark ? '0 12px 30px rgba(0,0,0,0.4)' : '0 8px 24px rgba(59,130,246,0.15)',
                                                                                    zIndex: 20,
                                                                                    minWidth: 220,
                                                                                    maxWidth: 360,
                                                                                }}
                                                                            >
                                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                                                    {(bestWorkLines && bestWorkLines.length > 0) || (bestCollegeLines && bestCollegeLines.length > 0) ? (
                                                                                        <>
                                                                                            <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.03em', color: isDark ? '#F59E0B' : '#2563eb' }}>
                                                                                                Best person background
                                                                                            </span>
                                                                                            {bestWorkLines && bestWorkLines.length > 0 && bestWorkLines.map((row, i) => (
                                                                                                <div key={`${row.name}-best-work-${i}`} style={{ fontSize: 11, color: isDark ? 'rgba(255,255,255,0.85)' : '#334155', lineHeight: 1.4 }}>
                                                                                                    <span style={{ fontWeight: 700 }}>{row.name}</span>
                                                                                                    {row.role ? ` · ${row.role}` : ''}
                                                                                                    {row.tenure ? ` · ${row.tenure}` : ''}
                                                                                                </div>
                                                                                            ))}
                                                                                            {bestCollegeLines && bestCollegeLines.length > 0 && bestCollegeLines.map((row, i) => (
                                                                                                <div key={`${row.name}-best-college-${i}`} style={{ fontSize: 11, color: isDark ? 'rgba(255,255,255,0.85)' : '#334155', lineHeight: 1.4 }}>
                                                                                                    <span style={{ fontWeight: 700 }}>{row.name}</span>
                                                                                                    {row.role ? ` · ${row.role}` : ''}
                                                                                                    {row.tenure ? ` · ${row.tenure}` : ''}
                                                                                                </div>
                                                                                            ))}
                                                                                        </>
                                                                                    ) : (individualLines && individualLines.length > 0) ? (
                                                                                        <>
                                                                                            <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.03em', color: isDark ? '#F59E0B' : '#2563eb' }}>
                                                                                                Best person background
                                                                                            </span>
                                                                                            {individualLines.map((row, i) => (
                                                                                                <div key={`${row.name}-single-${i}`} style={{ fontSize: 11, color: isDark ? 'rgba(255,255,255,0.85)' : '#334155', lineHeight: 1.4 }}>
                                                                                                    <span style={{ fontWeight: 700 }}>{row.name}</span>
                                                                                                    {row.role ? ` · ${row.role}` : ''}
                                                                                                    {row.tenure ? ` · ${row.tenure}` : ''}
                                                                                                </div>
                                                                                            ))}
                                                                                        </>
                                                                                    ) : (
                                                                                        tagRows.map((t, i) => (
                                                                                            <div key={`${t.label}-detail-${i}`} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                                                                <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.03em', color: isDark ? '#F59E0B' : '#2563eb' }}>
                                                                                                    {t.label}
                                                                                                </span>
                                                                                                {t.detail && (
                                                                                                    <span style={{ fontSize: 11, color: isDark ? 'rgba(255,255,255,0.8)' : '#475569', lineHeight: 1.4 }}>
                                                                                                        {t.detail}
                                                                                                    </span>
                                                                                                )}
                                                                                            </div>
                                                                                        ))
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })()}
                                                        </div>
                                                        {/* right: badge + stat */}
                                                        <div className="insights-right" style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                                                            {meta.badge && (
                                                                <span style={{
                                                                    padding: '3px 10px', borderRadius: 6, fontSize: 10, fontWeight: 800,
                                                                    background: `${meta.badgeColor}20`, color: meta.badgeColor,
                                                                    border: `1px solid ${meta.badgeColor}40`, letterSpacing: '0.06em'
                                                                }}>
                                                                    {meta.badge}
                                                                </span>
                                                            )}
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                <span className="insights-right-label" style={{ fontSize: 14, fontWeight: 700, color: rightColor, minWidth: 80, textAlign: 'right' }}>
                                                                    {rightLabel}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        {/* CTA buttons */}
                                                        <div className="insights-cta" style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                                                            {(() => {
                                                                const isPanelOpen = panelCompany?.destOrgId === company.organization_id;
                                                                return (
                                                                    <button
                                                                        onClick={() => {
                                                                            if (isLockedRow) {
                                                                                navigate('/pricing', { state: { ...pricingNavState, from: 'find_my_contact_locked' } });
                                                                                return;
                                                                            }
                                                                            if (isPanelOpen) setPanelCompany(null);
                                                                            else setPanelCompany(buildPanelParamsForCompany(company));
                                                                        }}
                                                                        className="insights-cta-button"
                                                                        style={{
                                                                            padding: '8px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700,
                                                                            background: isPanelOpen
                                                                                ? 'rgba(239,68,68,0.08)'
                                                                                : (isDark ? 'rgba(255,255,255,0.07)' : 'rgba(59,130,246,0.08)'),
                                                                            color: isPanelOpen
                                                                                ? '#EF4444'
                                                                                : (isDark ? 'rgba(255,255,255,0.8)' : '#475569'),
                                                                            cursor: 'pointer',
                                                                            display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
                                                                            transition: 'all 0.15s',
                                                                            border: isPanelOpen
                                                                                ? '1px solid rgba(239,68,68,0.2)'
                                                                                : (isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(59,130,246,0.1)'),
                                                                            opacity: isLockedRow ? 0.6 : 1,
                                                                        }}
                                                                        onMouseEnter={e => {
                                                                            if (!isPanelOpen) {
                                                                                e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(59,130,246,0.15)';
                                                                                e.currentTarget.style.color = isDark ? '#fff' : '#1e40af';
                                                                            } else {
                                                                                e.currentTarget.style.background = 'rgba(239,68,68,0.14)';
                                                                            }
                                                                        }}
                                                                        onMouseLeave={e => {
                                                                            if (!isPanelOpen) {
                                                                                e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(59,130,246,0.08)';
                                                                                e.currentTarget.style.color = isDark ? 'rgba(255,255,255,0.8)' : '#475569';
                                                                            } else {
                                                                                e.currentTarget.style.background = 'rgba(239,68,68,0.08)';
                                                                            }
                                                                        }}
                                                                    >
                                                                        {isLockedRow
                                                                            ? <><Lock size={12} /> Find my contact</>
                                                                            : (isPanelOpen ? <><X size={12} /> Close</> : <>Find my contact <ChevronRight size={12} /></>)
                                                                        }
                                                                    </button>
                                                                );
                                                            })()}
                                                            <button
                                                                onClick={() => {
                                                                    if (isLockedRow) {
                                                                        navigate('/pricing', { state: { ...pricingNavState, from: 'find_open_roles_locked' } });
                                                                        return;
                                                                    }
                                                                    handleFindOpenRoles(company);
                                                                }}
                                                                className="insights-cta-button insights-cta-primary"
                                                                style={{
                                                                    padding: '8px 14px', borderRadius: 10, fontSize: 12, fontWeight: 700,
                                                                    background: isDark
                                                                        ? 'linear-gradient(135deg, #F59E0B, #D97706)'
                                                                        : 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                                                                    color: isDark ? '#1C1917' : '#ffffff',
                                                                    cursor: 'pointer',
                                                                    display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
                                                                    textDecoration: 'none', transition: 'transform 0.15s',
                                                                    border: 'none',
                                                                    opacity: isLockedRow ? 0.6 : 1,
                                                                }}
                                                                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
                                                                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                                                            >
                                                                {isLockedRow
                                                                    ? <><Lock size={12} /> Find open roles</>
                                                                    : <>Find open roles <ExternalLink size={11} /></>
                                                                }
                                                            </button>
                                                        </div>
                                                    </motion.div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </motion.div>
            )}
            <AnimatePresence>
                {selectedCount > 0 && (
                    <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 20, opacity: 0 }}
                        className="insights-bottom-bar"
                        style={{
                            position: 'fixed', left: 0, right: 0, bottom: 24,
                            zIndex: 100, padding: '0 24px',
                            display: 'flex', justifyContent: 'center', pointerEvents: 'none'
                        }}
                    >
                        <div className="insights-bottom-bar-inner" style={{
                            maxWidth: 800, width: '100%',
                            background: isDark ? 'rgba(13,16,25,0.95)' : 'rgba(255,255,255,0.95)', backdropFilter: 'blur(12px)',
                            borderRadius: 24, border: `1px solid ${isDark ? 'rgba(245,158,11,0.22)' : 'rgba(37,99,235,0.22)'}`,
                            boxShadow: isDark ? '0 12px 32px rgba(0,0,0,0.5)' : '0 12px 32px rgba(59,130,246,0.15)',
                            padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 16,
                            pointerEvents: 'auto'
                        }}>
                            <div style={{
                                width: 44, height: 44, borderRadius: 16,
                                background: accentColor, color: isDark ? '#1A1612' : '#ffffff',
                                fontWeight: 800, fontSize: 18,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: isDark ? '0 4px 12px rgba(245,158,11,0.3)' : '0 4px 12px rgba(37,99,235,0.3)'
                            }}>
                                {selectedCount}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ fontSize: 15, fontWeight: 700, color: isDark ? '#fff' : '#1e293b', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {selectedSummaryText}
                                </p>
                                <p style={{ fontSize: 12, color: accentColor, margin: '2px 0 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <Sparkles size={11} /> Ready to find jobs
                                </p>
                            </div>
                            <div className="insights-bottom-actions" style={{ display: 'flex', gap: 10 }}>
                                <button
                                    onClick={clearSelectedCards}
                                    style={{
                                        padding: '0 16px', height: 40, borderRadius: 12,
                                        background: isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9',
                                        border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid #cbd5e1',
                                        color: isDark ? 'rgba(255,255,255,0.7)' : '#475569', fontSize: 13, fontWeight: 700,
                                        cursor: 'pointer', transition: 'all 0.15s'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}
                                    onMouseLeave={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9'}
                                >
                                    Clear
                                </button>
                                <button
                                    onClick={() => {
                                        setCurrentCompanyForJobs(null); // use multi-selection
                                        setIsRoleModalOpen(true);
                                    }}
                                    style={{
                                        padding: '0 20px', height: 40, borderRadius: 12,
                                        background: isDark ? 'linear-gradient(135deg, #F59E0B, #D97706)' : 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                                        color: isDark ? '#1A1612' : '#ffffff', fontSize: 13, fontWeight: 800,
                                        border: 'none', cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', gap: 6,
                                        boxShadow: isDark ? '0 4px 12px rgba(245,158,11,0.25)' : '0 4px 12px rgba(37,99,235,0.25)'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
                                    onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                                >
                                    <span style={{ position: 'relative', display: 'flex', height: 8, width: 8 }}>
                                        <span style={{ position: 'absolute', height: '100%', width: '100%', borderRadius: '50%', background: isDark ? '#1A1612' : '#ffffff', opacity: 0.75, animation: 'ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite' }} />
                                        <span style={{ position: 'relative', height: 8, width: 8, borderRadius: '50%', background: isDark ? '#1A1612' : '#ffffff' }} />
                                    </span>
                                    Find Jobs
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
            {/* Role Selection Modal */}
            <AnimatePresence>
                {isRoleModalOpen && (
                    <div style={{
                        position: 'fixed', inset: 0, zIndex: 110, display: 'flex',
                        alignItems: 'flex-end', justifyContent: 'center', p: 0,
                        background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)'
                    }}>
                        <motion.div
                            initial={{ y: '100%' }}
                            animate={{ y: 0 }}
                            exit={{ y: '100%' }}
                            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                            style={{
                                background: isDark ? '#0d1019f2' : '#ffffff', borderRadius: '40px 40px 0 0',
                                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', width: '100%', maxWidth: 672,
                                overflow: 'hidden', borderTop: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)'
                            }}
                        >
                            {/* Handle bars */}
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 16, paddingBottom: 8 }}>
                                <div style={{ width: 48, height: 6, background: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.15)', borderRadius: 999 }} />
                            </div>
                            <div style={{ padding: '16px 32px 32px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                                    <div style={{ width: 40, height: 40, borderRadius: 12, background: isDark ? 'rgba(245,158,11,0.15)' : 'rgba(37,99,235,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Search size={20} color={accentColor} />
                                    </div>
                                    <h2 style={{ fontSize: 24, fontWeight: 'bold', color: isDark ? 'rgba(255,255,255,0.9)' : '#0f172a', fontFamily: "'Playfair Display', serif", margin: 0 }}>
                                        What role are you looking for?
                                    </h2>
                                </div>
                                <p style={{ fontSize: 14, color: isDark ? 'rgba(255,255,255,0.5)' : '#64748b', marginBottom: 32 }}>
                                    Searching at <span style={{ fontWeight: 600, color: isDark ? 'rgba(255,255,255,0.85)' : '#1e293b' }}>
                                        {currentCompanyForJobs || (selectedCount === 1 ? selectedCompanyNames[0] : `${selectedCount} companies`)}
                                    </span>
                                </p>
                                <div style={{ position: 'relative', marginBottom: 24 }}>
                                    <Search size={20} style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8' }} />
                                    <input
                                        autoFocus
                                        type="text"
                                        value={roleInput}
                                        onChange={(e) => setRoleInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && roleInput.trim()) {
                                                if (!searchRoles.includes(roleInput.trim())) {
                                                    updateSearchRoles([...searchRoles, roleInput.trim()]);
                                                }
                                                setRoleInput('');
                                            }
                                        }}
                                        placeholder="Search or type a role..."
                                        style={{
                                            width: '100%', paddingLeft: 48, paddingRight: 16, height: 56,
                                            background: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc', border: isDark ? '1px solid rgba(255,255,255,0.15)' : '1px solid #cbd5e1',
                                            borderRadius: 16, color: isDark ? 'rgba(255,255,255,0.9)' : '#0f172a', outline: 'none', fontSize: 16,
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                    {roleInput.trim() && (
                                        <button
                                            onClick={() => {
                                                if (!searchRoles.includes(roleInput.trim())) {
                                                    updateSearchRoles([...searchRoles, roleInput.trim()]);
                                                }
                                                setRoleInput('');
                                            }}
                                            style={{
                                                position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                                                height: 32, padding: '0 12px', background: accentColor, color: isDark ? '#1A1612' : '#ffffff',
                                                fontSize: 12, fontWeight: 700, borderRadius: 8, border: 'none', cursor: 'pointer'
                                            }}
                                        >
                                            Add
                                        </button>
                                    )}
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 32, minHeight: 52 }}>
                                    {searchRoles.map((role) => (
                                        <button
                                            key={role}
                                            onClick={() => updateSearchRoles(searchRoles.filter(r => r !== role))}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px',
                                                background: isDark ? 'rgba(245,158,11,0.1)' : 'rgba(37,99,235,0.1)', color: accentColor,
                                                borderRadius: 999, fontSize: 13, fontWeight: 700,
                                                border: isDark ? '1px solid rgba(245,158,11,0.25)' : '1px solid rgba(37,99,235,0.25)', cursor: 'pointer',
                                                transition: 'all 0.15s'
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(245,158,11,0.18)' : 'rgba(37,99,235,0.18)'}
                                            onMouseLeave={e => e.currentTarget.style.background = isDark ? 'rgba(245,158,11,0.1)' : 'rgba(37,99,235,0.1)'}
                                        >
                                            {role}
                                            <X size={12} style={{ opacity: 0.7 }} />
                                        </button>
                                    ))}
                                    {searchRoles.length === 0 && (
                                        <div style={{
                                            display: 'flex', alignItems: 'center', gap: 8,
                                            padding: '10px 14px', borderRadius: 12, width: '100%',
                                            background: isDark ? 'rgba(255,255,255,0.03)' : '#f1f5f9', border: isDark ? '1px dashed rgba(255,255,255,0.1)' : '1px dashed #cbd5e1'
                                        }}>
                                            <PlusCircle size={14} color={isDark ? "rgba(255,255,255,0.25)" : "#94a3b8"} />
                                            <p style={{ fontSize: 13, color: isDark ? 'rgba(255,255,255,0.3)' : '#64748b', margin: 0 }}>
                                                Type a role above and press <kbd style={{ background: isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0', border: isDark ? '1px solid rgba(255,255,255,0.15)' : '1px solid #cbd5e1', borderRadius: 4, padding: '1px 5px', fontSize: 11, fontFamily: 'monospace' }}>Enter</kbd> to add it as a tag
                                            </p>
                                        </div>
                                    )}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingBottom: 32 }}>
                                    <button
                                        disabled={searchRoles.length === 0}
                                        onClick={() => openLinkedInJobsForSelection(searchRoles)}
                                        style={{
                                            height: 56, width: '100%', borderRadius: 16, fontWeight: 700,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            cursor: searchRoles.length > 0 ? 'pointer' : 'not-allowed',
                                            background: searchRoles.length > 0 ? accentColor : (isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'),
                                            color: searchRoles.length > 0 ? (isDark ? '#1A1612' : '#ffffff') : (isDark ? 'rgba(255,255,255,0.35)' : '#94a3b8'),
                                            border: 'none', fontSize: 16
                                        }}
                                    >
                                        {searchRoles.length > 0
                                            ? `Find open roles at ${currentCompanyForJobs || (selectedCount === 1 ? selectedCompanyNames[0] : `${selectedCount} companies`)}`
                                            : 'Select at least one role'}
                                    </button>
                                    <button
                                        onClick={() => {
                                            setIsRoleModalOpen(false);
                                            setRoleInput('');
                                        }}
                                        style={{
                                            height: 48, width: '100%', background: 'none', border: 'none',
                                            color: isDark ? 'rgba(255,255,255,0.55)' : '#64748b', fontWeight: 600, cursor: 'pointer',
                                            fontSize: 14
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
            {/* ── Company people side panel ── */}
            <CompanyPeoplePanel
                isOpen={panelCompany !== null}
                onClose={() => setPanelCompany(null)}
                sourceOrgId={panelCompany?.sourceOrgId}
                sourceCompanyName={panelCompany?.sourceCompanyName}
                destOrgId={panelCompany?.destOrgId}
                companyName={panelCompany?.companyName}
                initialHop={panelCompany?.hop || 1}
                startYear={panelCompany?.startYear}
                endYear={panelCompany?.endYear}
                role={panelCompany?.role || ''}
                location={panelCompany?.location || ''}
                connectionFilters={panelCompany?.connectionFilters}
                crossMatchedEmployeeIds={panelCompany?.crossMatchedEmployeeIds || []}
                matchedEmployeeId={panelCompany?.matchedEmployeeId}
                matchedEmployeeName={panelCompany?.matchedEmployeeName}
                initialOuterTab={panelCompany?.initialOuterTab}
                onOpenNewTab={() => {
                    if (!panelCompany) return;
                    const urlParams = new URLSearchParams();
                    if (panelCompany.sourceOrgId != null) urlParams.set('sourceOrgId', String(panelCompany.sourceOrgId));
                    if (panelCompany.sourceCompanyName) urlParams.set('sourceCompanyName', String(panelCompany.sourceCompanyName));
                    if (panelCompany.destOrgId != null) urlParams.set('destOrgId', String(panelCompany.destOrgId));
                    if (panelCompany.companyName) urlParams.set('companyName', String(panelCompany.companyName));
                    if (panelCompany.hop != null) urlParams.set('hop', String(panelCompany.hop));
                    if (panelCompany.startYear) urlParams.set('startYear', String(panelCompany.startYear));
                    if (panelCompany.endYear) urlParams.set('endYear', String(panelCompany.endYear));
                    if (panelCompany.role) urlParams.set('role', String(panelCompany.role));
                    if (panelCompany.location) urlParams.set('location', String(panelCompany.location));
                    if (panelCompany.connectionFilters && typeof panelCompany.connectionFilters === 'object') {
                        urlParams.set('cf', JSON.stringify(panelCompany.connectionFilters));
                    }
                    window.open(`/company-details?${urlParams.toString()}`, '_blank', 'noopener,noreferrer');
                }}
            />
            <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @media (max-width: 720px) {
          .insights-header {
            padding: 0 12px !important;
          }
          .insights-header-left {
            gap: 8px !important;
            min-width: 0;
          }
          .insights-header-brand {
            display: none;
          }
          .insights-mode-pill {
            font-size: 10px !important;
            padding: 3px 10px !important;
            max-width: 140px;
          }
          .insights-mode-pill span {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            display: block;
          }
          .insights-content {
            padding: 16px 12px 40px !important;
          }
          .insights-list-item {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 12px !important;
          }
          .insights-avatar {
            width: 40px !important;
            height: 40px !important;
            font-size: 16px !important;
          }
          .insights-title {
            font-size: 14px !important;
          }
          .insights-subtext {
            font-size: 11px !important;
          }
          .insights-right {
            width: 100% !important;
            justify-content: space-between !important;
          }
          .insights-right-label {
            min-width: 0 !important;
            text-align: left !important;
          }
          .insights-cta {
            width: 100% !important;
            flex-direction: column !important;
          }
          .insights-cta-button {
            width: 100% !important;
            justify-content: center !important;
            white-space: normal !important;
          }
          .insights-filter {
            width: 100% !important;
            display: block !important;
          }
          .insights-filter-button {
            width: 100% !important;
            justify-content: space-between !important;
          }
          .insights-filter-menu {
            width: 100% !important;
          }
          .insights-bottom-bar {
            padding: 0 14px !important;
            bottom: 16px !important;
          }
          .insights-bottom-bar-inner {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 10px !important;
            padding: 12px 14px !important;
            border-radius: 18px !important;
          }
          .insights-bottom-actions {
            width: 100% !important;
            flex-direction: column !important;
          }
          .insights-bottom-actions button {
            width: 100% !important;
          }
        }
      `}</style>
        </div>
    );
};
export default InsightsList;
